<template>
  <div
    v-if="!isRippleAdmin && developerMode == false"
    class="integration-dashboard"
    :class="{ 'integration-dashboard--fullscreen': isFullscreen }"
  >
    <base-header type="pattern" class="pb-6 pb-8 pt-5 pt-md-8">
      <!-- Page heading: gives the support team immediate context -->
      <div class="page-heading mb-4">
        <div class="page-heading-text">
          <h1 class="page-title">Integration Dashboard</h1>
          <p class="page-subtitle">
            Monitor the status of each connected integration. Select a tab below to view its details.
          </p>
        </div>
        <a-button class="fullscreen-toggle" @click="toggleFullscreen">
          <i :class="isFullscreen ? 'fas fa-compress' : 'fas fa-expand'" aria-hidden="true"></i>
          {{ isFullscreen ? 'Exit full screen' : 'Full screen' }}
        </a-button>
      </div>

      <a-tabs v-model="activeTab" type="card" size="large" class="integration-tabs">
        <a-tab-pane v-for="integration in integrations" :key="integration.key">
          <!-- Readable tab label with an icon -->
          <span slot="tab" class="tab-label">
            <i :class="integration.icon" class="tab-icon" aria-hidden="true"></i>
            {{ integration.name }}
          </span>

          <div class="integration-panel">
            <!-- Panel header with name, description and status badge -->
            <div class="panel-header">
              <div>
                <h2 class="panel-title">{{ integration.name }}</h2>
                <p class="panel-description">{{ integration.description }}</p>
              </div>
              <span class="status-badge" :class="`status-${integration.status}`">
                <span class="status-dot" aria-hidden="true"></span>
                {{ statusLabel(integration.status) }}
              </span>
            </div>

            <!-- ============ SYNC FLOWS (BoxMe & NetSuite) ============ -->
            <div v-if="integration.key === 'boxme' || integration.key === 'netsuite'" class="panel-body sync-flows-body">
              <div class="sync-flows-head">
                <h3 class="sync-flows-title">Sync Flows</h3>
                <a-button size="small" icon="reload" :loading="syncFlows.loading" @click="fetchSyncFlows">Refresh</a-button>
              </div>

              <a-spin :spinning="syncFlows.loading">
                <div v-if="groupedFlows(integration.key).length === 0 && !syncFlows.loading" class="sync-flows-empty">
                  No sync flows configured for {{ integration.name }}.
                </div>

                <!-- Grouped by direction, e.g. "BXI → NetSuite" / "NetSuite → BXI" -->
                <div v-for="group in groupedFlows(integration.key)" :key="group.label" class="flow-group">
                  <div class="flow-group-label" @click="toggleFlowGroup(group.label)">
                    <i
                      class="fas flow-group-caret"
                      :class="collapsedGroups.includes(group.label) ? 'fa-chevron-right' : 'fa-chevron-down'"
                      aria-hidden="true"
                    ></i>
                    <i class="fas fa-arrow-right-arrow-left" aria-hidden="true"></i>
                    {{ group.label }}
                    <span class="flow-group-count">{{ flowGroupCount(integration.key, group) }} flow{{ flowGroupCount(integration.key, group) === 1 ? '' : 's' }}</span>
                  </div>

                  <div
                    v-show="!collapsedGroups.includes(group.label)"
                    v-for="flow in group.flows"
                    :key="flow.key"
                    class="flow-row-wrap"
                  >
                    <div
                      class="flow-row"
                      :class="{ 'flow-row--has-error': flow.errorCount > 0, 'flow-row--expanded': expandedFlows.includes(flow.key) }"
                      @click="toggleFlow(flow.key)"
                    >
                      <span class="flow-caret">
                        <i :class="expandedFlows.includes(flow.key) ? 'fas fa-chevron-down' : 'fas fa-chevron-right'" aria-hidden="true"></i>
                      </span>
                      <span class="flow-status-dot" :class="`flow-status-${flow.status}`" aria-hidden="true"></span>
                      <span class="flow-entity">{{ flow.entity }}</span>
                      <span class="flow-counts">
                        <a-tooltip title="Synced successfully">
                          <span class="flow-count flow-count-success">
                            <i class="fas fa-check" aria-hidden="true"></i> {{ flow.successCount }}
                          </span>
                        </a-tooltip>
                        <a-tooltip v-if="flow.pendingCount > 0" title="Pending — not yet synced">
                          <span class="flow-count flow-count-pending">
                            <i class="fas fa-clock" aria-hidden="true"></i> {{ flow.pendingCount }}
                          </span>
                        </a-tooltip>
                        <a-tooltip v-if="flow.errorCount > 0" title="Failed with errors">
                          <span class="flow-count flow-count-error">
                            <i class="fas fa-xmark" aria-hidden="true"></i> {{ flow.errorCount }}
                          </span>
                        </a-tooltip>
                      </span>
                      <span class="flow-synced">{{ flow.lastSyncedAt ? formatTime(flow.lastSyncedAt) : '—' }}</span>
                    </div>

                    <!-- Expandable, lazy-loaded success/error records table -->
                    <div v-show="expandedFlows.includes(flow.key)" class="flow-records">
                      <div class="flow-records-filter">
                        <div class="flow-filter-field">
                          <label class="filter-label">Date Range</label>
                          <a-range-picker
                            v-model="flow.dateRange"
                            style="width: 260px"
                            format="YYYY-MM-DD"
                            @change="() => onFlowDateChange(flow)"
                          />
                        </div>
                        <div class="flow-filter-field">
                          <label class="filter-label">Status</label>
                          <a-select
                            v-model="flow.statusFilter"
                            style="width: 150px"
                            @change="() => onFlowDateChange(flow)"
                          >
                            <a-select-option value="">All</a-select-option>
                            <a-select-option value="SUCCESS">Success</a-select-option>
                            <a-select-option value="PENDING">Pending</a-select-option>
                            <a-select-option value="ERROR">Error</a-select-option>
                          </a-select>
                        </div>
                        <div v-if="flow.key === 'netsuite_pos_sales'" class="flow-filter-field">
                          <label class="filter-label">POS Mode</label>
                          <a-select
                            v-model="flow.posMode"
                            style="width: 150px"
                            @change="() => onFlowDateChange(flow)"
                          >
                            <a-select-option value="offline">Offline</a-select-option>
                            <a-select-option value="online">Online</a-select-option>
                            <a-select-option value="all">All</a-select-option>
                          </a-select>
                        </div>
                        <div class="flow-filter-actions">
                          <a-button
                            type="primary"
                            icon="download"
                            :loading="flow.exporting"
                            :disabled="flow.recordsPagination.total === 0"
                            @click="exportFlowCsv(flow)"
                          >
                            Export CSV
                          </a-button>
                        </div>
                      </div>

                      <a-table
                        :rowKey="r => r.id"
                        size="small"
                        :loading="flow.recordsLoading"
                        :columns="recordColumnsFor(flow)"
                        :dataSource="flow.records"
                        :pagination="flow.recordsPagination"
                        @change="(pg) => onFlowRecordsTableChange(flow, pg)"
                      >
                        <span slot="status" slot-scope="text">
                          <span class="log-status" :class="recordStatusClass(text)">
                            {{ text }}
                          </span>
                        </span>
                        <span slot="message" slot-scope="text">
                          <a-tooltip :title="text"><span class="message-cell">{{ text || '—' }}</span></a-tooltip>
                        </span>
                      </a-table>
                    </div>
                  </div>

                  <!-- Documents: an expandable row inside the "BXI → BoxMe" group (ordered first) -->
                  <div
                    v-if="integration.key === 'boxme' && group.label === 'BXI → BoxMe'"
                    v-show="!collapsedGroups.includes(group.label)"
                    class="flow-row-wrap flow-row-wrap--first"
                  >
                    <div class="flow-row" @click="toggleFlow('boxme_logs')">
                      <span class="flow-caret">
                        <i :class="expandedFlows.includes('boxme_logs') ? 'fas fa-chevron-down' : 'fas fa-chevron-right'" aria-hidden="true"></i>
                      </span>
                      <span class="flow-status-dot flow-status-up" aria-hidden="true"></span>
                      <span class="flow-entity">Documents</span>
                      <span class="flow-counts">
                        <span class="flow-count">{{ boxme.total }} record{{ boxme.total === 1 ? '' : 's' }}</span>
                      </span>
                      <span class="flow-synced">{{ lastRefreshedLabel ? 'Updated ' + lastRefreshedLabel : '—' }}</span>
                    </div>

                    <div v-show="expandedFlows.includes('boxme_logs')" class="flow-records">
                      <!-- Filter bar -->
              <div class="filter-bar">
                <div class="filter-field">
                  <label class="filter-label">Search</label>
                  <a-input
                    v-model="boxme.filters.search"
                    allow-clear
                    placeholder="Reference, Request Id... (min 5 characters)"
                    @input="onBoxmeSearchInput"
                  />
                </div>
                <div class="filter-field">
                  <label class="filter-label">Status</label>
                  <a-select v-model="boxme.filters.status" style="width: 100%" @change="applyBoxmeFilters">
                    <a-select-option value="">All</a-select-option>
                    <a-select-option value="SUCCESSFUL">Successful</a-select-option>
                    <a-select-option value="ERROR">Error</a-select-option>
                  </a-select>
                </div>
                <div class="filter-field">
                  <label class="filter-label">Document Type</label>
                  <a-select v-model="boxme.filters.documentType" style="width: 100%" @change="applyBoxmeFilters">
                    <a-select-option value="">All</a-select-option>
                    <a-select-option v-for="t in boxme.documentTypes" :key="t" :value="t">{{ t }}</a-select-option>
                  </a-select>
                </div>
                <div class="filter-field">
                  <label class="filter-label">Date Range</label>
                  <a-range-picker
                    v-model="boxme.filters.dateRange"
                    style="width: 100%"
                    format="YYYY-MM-DD"
                    @change="applyBoxmeFilters"
                  />
                </div>
                <div class="filter-field">
                  <label class="filter-label">Auto Reload</label>
                  <a-select v-model="autoReloadMinutes" style="width: 100%" @change="onAutoReloadChange">
                    <a-select-option v-for="opt in autoReloadOptions" :key="opt" :value="opt">
                      Every {{ opt }} minutes
                    </a-select-option>
                  </a-select>
                </div>
                <div class="filter-actions">
                  <a-button @click="resetBoxmeFilters">Reset</a-button>
                  <a-button type="primary" icon="download" :loading="boxme.exporting" :disabled="boxme.total === 0" @click="exportBoxmeCsv">
                    Export CSV
                  </a-button>
                </div>
              </div>

              <div class="boxme-meta" v-if="lastRefreshedLabel">
                <i class="fas fa-sync-alt" aria-hidden="true"></i>
                Last updated {{ lastRefreshedLabel }}
              </div>

              <div class="result-count">
                {{ boxme.total }} record{{ boxme.total === 1 ? '' : 's' }} found
              </div>

              <a-table
                rowKey="id"
                size="small"
                :loading="boxme.loading"
                :columns="boxmeColumns"
                :components="adjustableTableComponents"
                :dataSource="boxme.rows"
                :pagination="boxme.pagination"
                :scroll="{ x: 1070 }"
                @change="onBoxmeTableChange"
              >
                <span slot="status" slot-scope="text">
                  <span class="log-status" :class="text === 'ERROR' ? 'log-status-error' : 'log-status-success'">
                    {{ text }}
                  </span>
                </span>
                <span slot="message" slot-scope="text">
                  <a-tooltip :title="text">
                    <span class="message-cell">{{ text }}</span>
                  </a-tooltip>
                </span>
              </a-table>
                    </div><!-- /flow-records (logs body) -->
                  </div><!-- /flow-row-wrap (logs row) -->

                  <!-- Documents: an expandable row inside the "BXI → NetSuite" group (ordered first) -->
                  <div
                    v-if="integration.key === 'netsuite' && group.label === 'BXI → NetSuite'"
                    v-show="!collapsedGroups.includes(group.label)"
                    class="flow-row-wrap flow-row-wrap--first"
                  >
                    <div class="flow-row" @click="toggleFlow('netsuite_logs')">
                      <span class="flow-caret">
                        <i :class="expandedFlows.includes('netsuite_logs') ? 'fas fa-chevron-down' : 'fas fa-chevron-right'" aria-hidden="true"></i>
                      </span>
                      <span class="flow-status-dot flow-status-up" aria-hidden="true"></span>
                      <span class="flow-entity">Documents</span>
                      <span class="flow-counts">
                        <span class="flow-count">{{ netsuite.total }} record{{ netsuite.total === 1 ? '' : 's' }}</span>
                      </span>
                      <span class="flow-synced">—</span>
                    </div>

                    <div v-show="expandedFlows.includes('netsuite_logs')" class="flow-records">
                      <!-- Filter bar -->
                      <div class="filter-bar">
                        <div class="filter-field">
                          <label class="filter-label">Search</label>
                          <a-input
                            v-model="netsuite.filters.search"
                            allow-clear
                            placeholder="Reference, CAS No, Error... (min 5 characters)"
                            @input="onNetsuiteSearchInput"
                          />
                        </div>
                        <div class="filter-field">
                          <label class="filter-label">Status</label>
                          <a-select v-model="netsuite.filters.status" style="width: 100%" @change="applyNetsuiteFilters">
                            <a-select-option value="">All</a-select-option>
                            <a-select-option value="SUCCESSFUL">Successful</a-select-option>
                            <a-select-option value="ERROR">Error</a-select-option>
                          </a-select>
                        </div>
                        <div class="filter-field">
                          <label class="filter-label">Document Type</label>
                          <a-select v-model="netsuite.filters.documentType" style="width: 100%" @change="applyNetsuiteFilters">
                            <a-select-option value="">All</a-select-option>
                            <a-select-option v-for="t in netsuite.documentTypes" :key="t" :value="t">{{ t }}</a-select-option>
                          </a-select>
                        </div>
                        <div class="filter-field">
                          <label class="filter-label">Date Range</label>
                          <a-range-picker
                            v-model="netsuite.filters.dateRange"
                            style="width: 100%"
                            format="YYYY-MM-DD"
                            @change="applyNetsuiteFilters"
                          />
                        </div>
                        <div class="filter-actions">
                          <a-button @click="resetNetsuiteFilters">Reset</a-button>
                          <a-button type="primary" icon="download" :loading="netsuite.exporting" :disabled="netsuite.total === 0" @click="exportNetsuiteCsv">
                            Export CSV
                          </a-button>
                        </div>
                      </div>

                      <div class="result-count">
                        {{ netsuite.total }} record{{ netsuite.total === 1 ? '' : 's' }} found
                      </div>

                      <a-table
                        rowKey="id"
                        size="small"
                        :loading="netsuite.loading"
                        :columns="netsuiteColumns"
                        :components="adjustableTableComponents"
                        :dataSource="netsuite.rows"
                        :pagination="netsuite.pagination"
                        :scroll="{ x: 1070 }"
                        @change="onNetsuiteTableChange"
                      >
                        <span slot="status" slot-scope="text">
                          <span class="log-status" :class="text === 'ERROR' ? 'log-status-error' : 'log-status-success'">
                            {{ text }}
                          </span>
                        </span>
                        <span slot="message" slot-scope="text">
                          <a-tooltip :title="text">
                            <span class="message-cell">{{ text }}</span>
                          </a-tooltip>
                        </span>
                      </a-table>
                    </div><!-- /flow-records (netsuite logs body) -->
                  </div><!-- /flow-row-wrap (netsuite logs row) -->
                </div><!-- /flow-group -->
              </a-spin>
            </div><!-- /sync-flows-body -->

            <!-- ============ LYNEXUS: heartbeat / health of all platforms ============ -->
            <div v-else-if="integration.key === 'lynexus'" class="panel-body">
              <div class="heartbeat-toolbar">
                <span class="heartbeat-intro">Live health of every connected 3rd-party integration.</span>
                <span class="heartbeat-toolbar-right">
                  <span v-if="heartbeat.lastCheckedLabel" class="heartbeat-stamp">
                    <i class="fas fa-sync-alt" aria-hidden="true"></i>
                    Checked {{ heartbeat.lastCheckedLabel }}
                  </span>
                  <span class="heartbeat-autorefresh">
                    <span class="heartbeat-autorefresh-label">Auto refresh</span>
                    <a-select
                      v-model="heartbeat.autoRefreshMinutes"
                      size="small"
                      style="width: 120px"
                      @change="onHeartbeatAutoRefreshChange"
                    >
                      <a-select-option v-for="opt in heartbeat.autoRefreshOptions" :key="opt" :value="opt">
                        Every {{ opt }} min
                      </a-select-option>
                    </a-select>
                  </span>
                  <a-button
                    size="small"
                    icon="reload"
                    :loading="heartbeat.loading"
                    @click="fetchHeartbeat"
                  >
                    Refresh
                  </a-button>
                </span>
              </div>

              <a-spin :spinning="heartbeat.loading">
                <div v-if="!heartbeat.loading && heartbeat.items.length === 0" class="empty-state">
                  <i class="fas fa-heart-pulse empty-icon" aria-hidden="true"></i>
                  <h3 class="empty-title">No heartbeats yet</h3>
                  <p class="empty-text">
                    Health will appear here once Lynexus starts reporting for BoxMe, NetSuite, or Omisell.
                  </p>
                </div>
                <!-- Health summary: makes trouble obvious before you read any card -->
                <div v-if="heartbeat.items.length" class="hb-summary" :class="`hb-summary-${heartbeatOverallStatus}`">
                  <i class="hb-summary-icon" :class="heartbeatStatusIcon(heartbeatOverallStatus)" aria-hidden="true"></i>
                  <span class="hb-summary-text">
                    <template v-if="heartbeatCounts.down">
                      {{ heartbeatCounts.down }} integration{{ heartbeatCounts.down === 1 ? '' : 's' }} OFFLINE
                      <template v-if="heartbeatCounts.warning"> · {{ heartbeatCounts.warning }} degraded</template>
                      — needs attention
                    </template>
                    <template v-else-if="heartbeatCounts.warning">
                      {{ heartbeatCounts.warning }} integration{{ heartbeatCounts.warning === 1 ? '' : 's' }} degraded — check below
                    </template>
                    <template v-else-if="heartbeatCounts.up">
                      All {{ heartbeatCounts.up }} integration{{ heartbeatCounts.up === 1 ? '' : 's' }} online and healthy
                    </template>
                    <template v-else>
                      Waiting for health data
                    </template>
                  </span>
                </div>

                <div v-if="heartbeat.items.length" class="heartbeat-grid">
                  <div
                    v-for="hb in heartbeat.items"
                    :key="hb.key"
                    class="heartbeat-card"
                    :class="[`hb-${hb.status}`, { 'hb-alarm': hb.status === 'down' || hb.status === 'warning' }]"
                    role="status"
                    :aria-label="`${hb.name}: ${heartbeatStatusLabel(hb.status)}`"
                  >
                    <!-- Bold status banner: the first thing the eye lands on -->
                    <div class="hb-banner" :class="`hb-banner-${hb.status}`">
                      <i class="hb-banner-icon" :class="heartbeatStatusIcon(hb.status)" aria-hidden="true"></i>
                      <span class="hb-banner-label">{{ heartbeatStatusLabel(hb.status) }}</span>
                      <span class="hb-pulse" :class="`hb-pulse-${hb.status}`" aria-hidden="true"></span>
                    </div>

                    <div class="hb-head">
                      <div class="hb-identity">
                        <i :class="hb.icon" class="hb-icon" aria-hidden="true"></i>
                        <span class="hb-name">{{ hb.name }}</span>
                      </div>
                      <span class="hb-processed">
                        <strong>{{ hb.processed != null ? hb.processed : '—' }}</strong> processed
                      </span>
                    </div>

                    <ul class="hb-events">
                      <li class="hb-event">
                        <i class="fas fa-play hb-event-icon hb-event-start" aria-hidden="true"></i>
                        <span class="hb-event-label">Started</span>
                        <span class="hb-event-time">{{ hb.startedAt ? formatTime(hb.startedAt) : '—' }}</span>
                      </li>
                      <li class="hb-event">
                        <i class="fas fa-stop hb-event-icon hb-event-stop" aria-hidden="true"></i>
                        <span class="hb-event-label">Stopped</span>
                        <span class="hb-event-time">{{ hb.stoppedAt ? formatTime(hb.stoppedAt) : '—' }}</span>
                      </li>
                      <li class="hb-event">
                        <i class="fas fa-triangle-exclamation hb-event-icon hb-event-error" aria-hidden="true"></i>
                        <span class="hb-event-label">Last Error</span>
                        <span class="hb-event-time">{{ hb.lastErrorAt ? formatTime(hb.lastErrorAt) : 'None' }}</span>
                      </li>
                    </ul>

                    <p v-if="hb.lastErrorMessage" class="hb-error-message">
                      <a-tooltip :title="hb.lastErrorMessage">{{ hb.lastErrorMessage }}</a-tooltip>
                    </p>
                  </div>
                </div>
              </a-spin>

              <!-- ============ Heartbeat history table ============ -->
              <div class="heartbeat-history">
                <div class="section-collapse-header" @click="toggleHeartbeatHistory">
                  <i
                    class="fas section-collapse-icon"
                    :class="heartbeatHistory.collapsed ? 'fa-chevron-right' : 'fa-chevron-down'"
                    aria-hidden="true"
                  ></i>
                  <span class="section-collapse-title">Heartbeat History</span>
                  <span class="section-collapse-count">{{ heartbeatHistory.total }} record{{ heartbeatHistory.total === 1 ? '' : 's' }}</span>
                </div>

                <div v-show="!heartbeatHistory.collapsed" class="flow-records">
                  <div class="flow-records-filter">
                    <div class="flow-filter-field">
                      <label class="filter-label">Date Range</label>
                      <a-range-picker
                        v-model="heartbeatHistory.dateRange"
                        style="width: 260px"
                        format="YYYY-MM-DD"
                        @change="onHeartbeatHistoryFilter"
                      />
                    </div>
                    <div class="flow-filter-field">
                      <label class="filter-label">Name</label>
                      <a-select
                        v-model="heartbeatHistory.integrationKey"
                        style="width: 160px"
                        @change="onHeartbeatHistoryFilter"
                      >
                        <a-select-option value="">All</a-select-option>
                        <a-select-option value="boxme">BoxMe</a-select-option>
                        <a-select-option value="netsuite">NetSuite</a-select-option>
                        <a-select-option value="omisell">Omisell</a-select-option>
                      </a-select>
                    </div>
                    <div class="flow-filter-field">
                      <label class="filter-label">Status</label>
                      <a-select
                        v-model="heartbeatHistory.status"
                        style="width: 150px"
                        @change="onHeartbeatHistoryFilter"
                      >
                        <a-select-option value="">All</a-select-option>
                        <a-select-option value="up">Online</a-select-option>
                        <a-select-option value="warning">Degraded</a-select-option>
                        <a-select-option value="down">Offline</a-select-option>
                        <a-select-option value="unknown">Unknown</a-select-option>
                      </a-select>
                    </div>
                    <div class="flow-filter-actions">
                      <a-button icon="line-chart" @click="openHeartbeatGraph">View Graph</a-button>
                    </div>
                  </div>

                  <a-table
                    :rowKey="r => r.id"
                    size="small"
                    :loading="heartbeatHistory.loading"
                    :columns="heartbeatHistoryColumns"
                    :dataSource="heartbeatHistory.rows"
                    :pagination="heartbeatHistory.pagination"
                    @change="onHeartbeatHistoryTableChange"
                  >
                    <span slot="status" slot-scope="text">
                      <span class="log-status" :class="`log-status-hb-${text}`">
                        {{ heartbeatStatusLabel(text) }}
                      </span>
                    </span>
                    <span slot="message" slot-scope="text">
                      <a-tooltip :title="text"><span class="message-cell">{{ text || '—' }}</span></a-tooltip>
                    </span>
                  </a-table>
                </div>
              </div>

              <!-- Heartbeat graph modal: processed count over time per integration -->
              <a-modal
                v-model="heartbeatGraph.visible"
                title="Heartbeat Graph"
                width="820px"
                :footer="null"
              >
                <div class="heartbeat-graph-toolbar">
                  <a-radio-group v-model="heartbeatGraph.mode" button-style="solid" size="small" @change="buildHeartbeatGraph">
                    <a-radio-button value="status">Status counts</a-radio-button>
                    <a-radio-button value="processed">Processed over time</a-radio-button>
                  </a-radio-group>
                </div>
                <a-spin :spinning="heartbeatGraph.loading">
                  <div v-if="heartbeatGraph.hasData" class="heartbeat-graph-wrap">
                    <bar-chart
                      v-if="heartbeatGraph.mode === 'status'"
                      :chart-data="heartbeatGraph.chartData"
                      :extra-options="heartbeatGraph.barOptions"
                      :height="320"
                    />
                    <line-chart
                      v-else
                      :chart-data="heartbeatGraph.chartData"
                      :extra-options="heartbeatGraph.lineOptions"
                      :height="320"
                    />
                  </div>
                  <div v-else class="empty-state">
                    <i class="fas fa-chart-line empty-icon" aria-hidden="true"></i>
                    <h3 class="empty-title">No data to chart</h3>
                    <p class="empty-text">No heartbeats match the current filters.</p>
                  </div>
                </a-spin>
              </a-modal>
            </div>

            <!-- ============ POS: reconciliation table ============ -->
            <div v-else-if="integration.key === 'pos'" class="panel-body">
              <div class="section-collapse-header" @click="posRecon.collapsed = !posRecon.collapsed">
                <i
                  class="fas section-collapse-icon"
                  :class="posRecon.collapsed ? 'fa-chevron-right' : 'fa-chevron-down'"
                  aria-hidden="true"
                ></i>
                <span class="section-collapse-title">POS Reconciliation</span>
                <span class="section-collapse-count">{{ posRecon.pagination.total }} record{{ posRecon.pagination.total === 1 ? '' : 's' }}</span>
              </div>

              <div v-show="!posRecon.collapsed" class="flow-records">
                <div class="flow-records-filter">
                  <div class="flow-filter-field">
                    <label class="filter-label">Business Date</label>
                    <a-range-picker
                      v-model="posRecon.businessDate"
                      style="width: 260px"
                      format="YYYY-MM-DD"
                      @change="onPosReconFilter"
                    />
                  </div>
                  <div class="flow-filter-field">
                    <label class="filter-label">Site</label>
                    <a-input
                      v-model="posRecon.site"
                      allow-clear
                      placeholder="Min 5 characters"
                      style="width: 280px"
                      @change="onPosReconFilterDebounced"
                    />
                  </div>
                  <div class="flow-filter-field">
                    <label class="filter-label">Terminal</label>
                    <a-input
                      v-model="posRecon.terminal"
                      allow-clear
                      placeholder="Min 5 characters"
                      style="width: 280px"
                      @change="onPosReconFilterDebounced"
                    />
                  </div>
                  <div class="flow-filter-field">
                    <label class="filter-label">Status</label>
                    <a-select
                      v-model="posRecon.status"
                      style="width: 150px"
                      @change="onPosReconFilter"
                    >
                      <a-select-option value="">All</a-select-option>
                      <a-select-option value="COMPLETE">COMPLETE</a-select-option>
                      <a-select-option value="NO Z-READING">NO Z-READING</a-select-option>
                      <a-select-option value="MISSING">MISSING</a-select-option>
                    </a-select>
                  </div>
                  <div class="flow-filter-actions">
                    <span class="flow-count flow-count-success" v-if="posRecon.successCount > 0">
                      <i class="fas fa-check" aria-hidden="true"></i> {{ posRecon.successCount }} Complete
                    </span>
                    <span class="flow-count flow-count-pending" v-if="posRecon.pendingCount > 0">
                      <i class="fas fa-clock" aria-hidden="true"></i> {{ posRecon.pendingCount }} No Z-Reading
                    </span>
                    <span class="flow-count flow-count-error" v-if="posRecon.errorCount > 0">
                      <i class="fas fa-xmark" aria-hidden="true"></i> {{ posRecon.errorCount }} Missing
                    </span>
                  </div>
                </div>

                <a-table
                  :rowKey="r => r.terminal_id + '-' + r.business_date + '-' + (r.z_id || 'noz')"
                  size="small"
                  :loading="posRecon.loading"
                  :columns="posReconColumns"
                  :dataSource="posRecon.rows"
                  :pagination="posRecon.pagination"
                  @change="onPosReconTableChange"
                >
                  <span slot="status" slot-scope="text">
                    <span class="log-status" :class="text === 'COMPLETE' ? 'log-status-success' : (text === 'NO Z-READING' ? 'log-status-pending' : 'log-status-error')">
                      {{ text === 'COMPLETE' ? 'COMPLETE' : (text === 'NO Z-READING' ? 'NO Z-READING' : 'MISSING') }}
                    </span>
                  </span>
                </a-table>
              </div>
            </div>

            <!-- ============ OMISELL: document logs (ORIOSO, ORIOCR, OTHSIN) ============ -->
            <div v-else-if="integration.key === 'omisell'" class="panel-body">
              <div class="section-collapse-header" @click="omisell.collapsed = !omisell.collapsed">
                <i
                  class="fas section-collapse-icon"
                  :class="omisell.collapsed ? 'fa-chevron-right' : 'fa-chevron-down'"
                  aria-hidden="true"
                ></i>
                <span class="section-collapse-title">Documents</span>
                <span class="section-collapse-count">{{ omisell.total }} record{{ omisell.total === 1 ? '' : 's' }}</span>
              </div>

              <div v-show="!omisell.collapsed" class="flow-records">
                <!-- Filter bar -->
                <div class="filter-bar">
                  <div class="filter-field">
                    <label class="filter-label">Search</label>
                    <a-input
                      v-model="omisell.filters.search"
                      allow-clear
                      placeholder="Reference, Request Id... (min 5 characters)"
                      @input="onOmisellSearchInput"
                    />
                  </div>
                  <div class="filter-field">
                    <label class="filter-label">Status</label>
                    <a-select v-model="omisell.filters.status" style="width: 100%" @change="applyOmisellFilters">
                      <a-select-option value="">All</a-select-option>
                      <a-select-option value="SUCCESSFUL">Successful</a-select-option>
                      <a-select-option value="ERROR">Error</a-select-option>
                    </a-select>
                  </div>
                  <div class="filter-field">
                    <label class="filter-label">Print Status</label>
                    <a-select v-model="omisell.filters.printStatus" style="width: 100%" @change="applyOmisellFilters">
                      <a-select-option value="">All</a-select-option>
                      <a-select-option value="PENDING">Pending</a-select-option>
                      <a-select-option value="PRINTED">Printed</a-select-option>
                      <a-select-option value="ERROR">Error</a-select-option>
                    </a-select>
                  </div>
                  <div class="filter-field">
                    <label class="filter-label">Document Type</label>
                    <a-select v-model="omisell.filters.documentType" style="width: 100%" @change="applyOmisellFilters">
                      <a-select-option value="">All</a-select-option>
                      <a-select-option v-for="t in omisell.documentTypes" :key="t" :value="t">{{ t }}</a-select-option>
                    </a-select>
                  </div>
                  <div class="filter-field">
                    <label class="filter-label">Date Range</label>
                    <a-range-picker
                      v-model="omisell.filters.dateRange"
                      style="width: 100%"
                      format="YYYY-MM-DD"
                      @change="applyOmisellFilters"
                    />
                  </div>
                  <div class="filter-actions">
                    <a-button @click="resetOmisellFilters">Reset</a-button>
                    <a-button type="primary" icon="download" :loading="omisell.exporting" :disabled="omisell.total === 0" @click="exportOmisellCsv">
                      Export CSV
                    </a-button>
                  </div>
                </div>

                <div class="result-count">
                  {{ omisell.total }} record{{ omisell.total === 1 ? '' : 's' }} found
                </div>

                <a-table
                  rowKey="id"
                  size="small"
                  :loading="omisell.loading"
                  :columns="omisellColumns"
                  :components="adjustableTableComponents"
                  :dataSource="omisell.rows"
                  :pagination="omisell.pagination"
                  :scroll="{ x: 1070 }"
                  @change="onOmisellTableChange"
                >
                  <span slot="status" slot-scope="text">
                    <span class="log-status" :class="text === 'ERROR' ? 'log-status-error' : 'log-status-success'">
                      {{ text }}
                    </span>
                  </span>
                  <span slot="printStatus" slot-scope="text">
                    <span
                      v-if="text"
                      class="log-status"
                      :class="text === 'PRINTED' ? 'log-status-success' : (text === 'PENDING' ? 'log-status-pending' : 'log-status-error')"
                    >
                      {{ text }}
                    </span>
                    <span v-else>—</span>
                  </span>
                  <span slot="message" slot-scope="text">
                    <a-tooltip :title="text">
                      <span class="message-cell">{{ text }}</span>
                    </a-tooltip>
                  </span>
                </a-table>
              </div>
            </div>

            <!-- ============ Other integrations: empty-state placeholder ============ -->
            <div v-else class="panel-body">
              <div class="empty-state">
                <i :class="integration.icon" class="empty-icon" aria-hidden="true"></i>
                <h3 class="empty-title">No data to display yet</h3>
                <p class="empty-text">
                  {{ integration.name }} integration details will appear here once configured.
                </p>
              </div>
            </div>
          </div>
        </a-tab-pane>
      </a-tabs>
    </base-header>
  </div> <!-- end condition for user type-->
</template>

<script>
  import secureStorage from '../controllers/secureStorage.js'
  import HTTPRequest from '../controllers/httpRequest.js'
  import { mapState } from 'vuex'
  import moment from 'moment'
  import BarChart from '@/components/Charts/BarChart'
  import LineChart from '@/components/Charts/LineChart'

  // Header cell for the document log tables (BoxMe / NetSuite / Omisell).
  //  - Drag the right-edge handle to resize the column.
  //  - Drag the header itself onto another header to reorder the columns.
  // ant-design-vue calls a function-type header cell as (h, data, children);
  // the `resize` / `reorder` callbacks arrive in data.on and the column key in
  // data.attrs['data-colkey'] (both set by makeColumnsAdjustable below).
  const adjustableHeaderCell = (h, data, children) => {
    const colKey = data.attrs && data.attrs['data-colkey'];
    if (!colKey) {
      return h('th', data, children);
    }
    const handlers = data.on || {};

    const startResize = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const th = e.currentTarget.parentElement;
      const startX = e.clientX;
      const startWidth = th.offsetWidth;
      // Stop the header from starting a reorder drag while resizing
      th.setAttribute('draggable', 'false');
      // While dragging: resize(width, false) updates the live column width
      // without writing to localStorage on every pixel of movement.
      const onMove = (ev) => handlers.resize(Math.max(50, startWidth + ev.clientX - startX), false);
      const onUp = (ev) => {
        handlers.resize(Math.max(50, startWidth + ev.clientX - startX), true);
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        th.setAttribute('draggable', 'true');
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    };

    const thData = Object.assign({}, data, {
      class: [data.class, 'adjustable-th'],
      attrs: Object.assign({}, data.attrs, { draggable: 'true' }),
      on: {
        dragstart: (e) => {
          e.dataTransfer.effectAllowed = 'move';
          e.dataTransfer.setData('text/plain', colKey);
        },
        dragover: (e) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
          e.currentTarget.classList.add('adjustable-th--over');
        },
        dragleave: (e) => e.currentTarget.classList.remove('adjustable-th--over'),
        drop: (e) => {
          e.preventDefault();
          e.currentTarget.classList.remove('adjustable-th--over');
          const from = e.dataTransfer.getData('text/plain');
          if (from && from !== colKey) {
            handlers.reorder(from, colKey);
          }
        }
      }
    });

    return h('th', thData, [
      children,
      h('span', {
        class: 'adjustable-th__handle',
        on: { mousedown: startResize, click: (e) => e.stopPropagation() }
      })
    ]);
  };

  export default {
    components: {
      BarChart,
      LineChart
    },
    computed: {
      ...mapState(['loggedIn', 'endPoint', 'serverType']),

      // Tally of integrations per health state, for the summary banner.
      heartbeatCounts() {
        const counts = { up: 0, down: 0, warning: 0, unknown: 0 };
        (this.heartbeat.items || []).forEach(hb => {
          const s = counts.hasOwnProperty(hb.status) ? hb.status : 'unknown';
          counts[s] += 1;
        });
        return counts;
      },

      // Worst state wins, so the banner reflects the most urgent problem:
      // offline (down) > degraded (warning) > online (up) > unknown.
      heartbeatOverallStatus() {
        const c = this.heartbeatCounts;
        if (c.down) return 'down';
        if (c.warning) return 'warning';
        if (c.up) return 'up';
        return 'unknown';
      },
    },
    data() {
      return {
        activeTab: 'lynexus',
        isRippleAdmin: secureStorage.getItem("isRippleAdmin"),
        developerMode: secureStorage.getItem("developerMode"),
        boxmeSearchTimer: null,
        omisellSearchTimer: null,
        netsuiteSearchTimer: null,

        // Full screen mode for the whole dashboard
        isFullscreen: false,

        // Custom header cell that makes the log table columns resizable / reorderable
        adjustableTableComponents: { header: { cell: adjustableHeaderCell } },

        // Auto-reload: default every 5 minutes
        autoReloadMinutes: 5,
        autoReloadOptions: [5, 10, 20, 30, 45],
        autoReloadTimer: null,
        lastRefreshedAt: null,
        lastRefreshedLabel: '',
        clockTimer: null,

        // Collapsible state for the BoxMe logs table (expanded by default)
        boxmeCollapsed: false,

        // Sync flows (BoxMe & NetSuite): entity-level sync status + records.
        syncFlows: {
          loading: false,
          items: []
        },
        expandedFlows: [],
        // Collapsed direction groups (by label), e.g. "BXI → BoxMe"
        collapsedGroups: [],
        syncRecordColumns: [
          { title: 'Id', dataIndex: 'id', width: 140 },
          { title: 'Barcode', dataIndex: 'barcode', width: 200 },
          { title: 'Name', dataIndex: 'name', width: 500 },
          { title: 'Status', dataIndex: 'status', width: 110, scopedSlots: { customRender: 'status' } },
          { title: 'Synced At', dataIndex: 'syncedAt', width: 170 },
          { title: 'Message', dataIndex: 'message', scopedSlots: { customRender: 'message' } }
        ],

        // Lynexus heartbeat: per-integration lifecycle health.
        // Seeded with the three integrations in 'unknown' state; replaced by
        // live rows from GET integrations/heartbeat (fetchHeartbeat). Keys:
        //   status        : 'up' | 'down' | 'warning' | 'unknown'
        //   processed     : number of records processed in the last run
        //   startedAt     : ISO/date string when the last run started
        //   stoppedAt     : ISO/date string when the last run stopped
        //   lastErrorAt   : ISO/date string of the last error (null if none)
        //   lastErrorMessage : short error description (optional)
        heartbeat: {
          loading: false,
          lastCheckedAt: null,
          lastCheckedLabel: '',
          // Populated from GET integrations/heartbeat (fetchHeartbeat).
          items: [],
          // Auto-refresh (Lynexus tab only): default every 1 minute
          autoRefreshMinutes: 5,
          autoRefreshOptions: [5, 10, 20, 30],
          autoRefreshTimer: null
        },

        // Heartbeat history: full append-only log with filters + pagination
        heartbeatHistory: {
          collapsed: true,
          loading: false,
          loaded: false,
          rows: [],
          total: 0,
          dateRange: [moment().subtract(3, 'days'), moment()],
          integrationKey: '',
          status: '',
          pagination: { current: 1, pageSize: 10, total: 0, showSizeChanger: true }
        },

        // POS reconciliation table (POS tab)
        posRecon: {
          collapsed: false,
          loaded: false,
          loading: false,
          rows: [],
          businessDate: [moment(), moment()],
          status: '',
          site: '',
          terminal: '',
          successCount: 0,
          pendingCount: 0,
          errorCount: 0,
          pagination: { current: 1, pageSize: 10, total: 0, showSizeChanger: true }
        },
        posReconFilterTimer: null,

        posReconColumns: [
          { title: 'Site', dataIndex: 'site_name', width: 240 },
          { title: 'Terminal', dataIndex: 'terminal_code', width: 140 },
          { title: 'Business Date', dataIndex: 'business_date', width: 140 },
          { title: 'POS Docs', dataIndex: 'pos_doc_count', width: 110, align: 'right' },
          { title: 'Z Trans', dataIndex: 'z_trans_count', width: 110, align: 'right' },
          { title: 'Status', dataIndex: 'status', width: 120, scopedSlots: { customRender: 'status' } }
        ],

        // Heartbeat graph modal (toggle: status counts bar / processed line)
        heartbeatGraph: {
          visible: false,
          loading: false,
          hasData: false,
          mode: 'status',
          rawRows: [],
          chartData: { labels: [], datasets: [] },
          barOptions: {
            responsive: true,
            maintainAspectRatio: false,
            legend: { position: 'bottom' },
            scales: {
              yAxes: [{ ticks: { beginAtZero: true, precision: 0 }, scaleLabel: { display: true, labelString: 'Heartbeats' } }],
              xAxes: [{ gridLines: { display: false } }]
            }
          },
          lineOptions: {
            responsive: true,
            maintainAspectRatio: false,
            legend: { position: 'bottom' },
            scales: {
              yAxes: [{ ticks: { beginAtZero: true }, scaleLabel: { display: true, labelString: 'Processed' } }],
              xAxes: [{ ticks: { maxRotation: 45, minRotation: 0 }, gridLines: { display: false } }]
            }
          }
        },

        // BoxMe log table: filters, server-fetched rows and pagination state
        boxme: {
          loaded: false,
          loading: false,
          exporting: false,
          rows: [],
          total: 0,
          documentTypes: [],
          filters: {
            search: '',
            status: '',
            documentType: '',
            // Default to the last 3 days (3 days ago -> today)
            dateRange: [moment().subtract(3, 'days'), moment()]
          },
          pagination: {
            current: 1,
            pageSize: 10,
            total: 0,
            showSizeChanger: true
          }
        },

        boxmeColumns: [
          { title: 'Date', dataIndex: 'date', width: 100 },
          { title: 'Document Type', dataIndex: 'documentType', width: 130 },
          { title: 'Document Number', dataIndex: 'documentNumber', width: 150 },
          { title: 'CAS No', dataIndex: 'casNo', width: 140 },
          { title: 'Reference Code', dataIndex: 'referenceCode', width: 200 },
          { title: 'Status', dataIndex: 'status', width: 120, scopedSlots: { customRender: 'status' } },
          { title: 'Request Id', dataIndex: 'requestId', width: 200 },
          { title: 'Error', dataIndex: 'error', width: 160 },
          { title: 'Message', dataIndex: 'message', width: 260, scopedSlots: { customRender: 'message' } }
        ],

        // Omisell log table: filters, server-fetched rows and pagination state
        // (same shape as `boxme`, backed by GET integrations/omisell_logs,
        // limited to ORIOSO, ORIOCR, OTHSIN on the backend)
        omisell: {
          collapsed: false,
          loaded: false,
          loading: false,
          exporting: false,
          rows: [],
          total: 0,
          documentTypes: [],
          filters: {
            search: '',
            status: '',
            printStatus: '',
            documentType: '',
            // Default to the last 3 days (3 days ago -> today)
            dateRange: [moment().subtract(3, 'days'), moment()]
          },
          pagination: {
            current: 1,
            pageSize: 10,
            total: 0,
            showSizeChanger: true
          }
        },

        // NetSuite document log table: filters, server-fetched rows and pagination
        // (backed by GET integrations/netsuite_logs, from document_external_logs)
        netsuite: {
          loaded: false,
          loading: false,
          exporting: false,
          rows: [],
          total: 0,
          documentTypes: [],
          filters: {
            search: '',
            status: '',
            documentType: '',
            // Default to the last 3 days (3 days ago -> today)
            dateRange: [moment().subtract(3, 'days'), moment()]
          },
          pagination: {
            current: 1,
            pageSize: 10,
            total: 0,
            showSizeChanger: true
          }
        },

        netsuiteColumns: [
          { title: 'Date', dataIndex: 'date', width: 100 },
          { title: 'Document Type', dataIndex: 'documentType', width: 130 },
          { title: 'Document Number', dataIndex: 'documentNumber', width: 150 },
          { title: 'CAS No', dataIndex: 'casNo', width: 140 },
          { title: 'Status', dataIndex: 'status', width: 120, scopedSlots: { customRender: 'status' } },
          { title: 'Reference Code', dataIndex: 'referenceCode', width: 200 },
          { title: 'Order source', dataIndex: 'orderSource', width: 140 },
          { title: 'SBU', dataIndex: 'sbu', width: 80 },
          { title: 'Error Slot', dataIndex: 'error', width: 100 },
          { title: 'Message', dataIndex: 'message', width: 320, scopedSlots: { customRender: 'message' } }
        ],

        omisellColumns: [
          { title: 'Date', dataIndex: 'date', width: 100 },
          { title: 'Document Type', dataIndex: 'documentType', width: 130 },
          { title: 'Document Number', dataIndex: 'documentNumber', width: 150 },
          { title: 'Reference Code', dataIndex: 'referenceCode', width: 200 },
          { title: 'Status', dataIndex: 'status', width: 120, scopedSlots: { customRender: 'status' } },
          { title: 'Print Status', dataIndex: 'printStatus', width: 120, scopedSlots: { customRender: 'printStatus' } },
          { title: 'SBU', dataIndex: 'sbu', width: 80 },
          { title: 'Request Id', dataIndex: 'requestId', width: 200 },
          { title: 'Error', dataIndex: 'error', width: 160 },
          { title: 'Message', dataIndex: 'message', width: 260, scopedSlots: { customRender: 'message' } }
        ],
        heartbeatHistoryColumns: [
          { title: 'Recorded At', dataIndex: 'recordedAt', width: 180 },
          { title: 'Name', dataIndex: 'name', width: 140 },
          { title: 'Status', dataIndex: 'status', width: 120, scopedSlots: { customRender: 'status' } },
          { title: 'Processed', dataIndex: 'processed', width: 110, align: 'right' },
          { title: 'Started', dataIndex: 'startedAt', width: 170 },
          { title: 'Stopped', dataIndex: 'stoppedAt', width: 170 },
          { title: 'Last Error', dataIndex: 'lastErrorMessage', scopedSlots: { customRender: 'message' } }
        ],
        // Single source of truth for the tabs. Set `status` to
        // 'connected', 'warning', or 'disconnected' per integration.
        integrations: [
          {
            key: 'lynexus',
            name: 'Lynexus',
            description: 'Central hub that manages integrations across all platforms.',
            icon: 'fas fa-sitemap',
            status: 'connected'
          },
          {
            key: 'pos',
            name: 'POS',
            description: 'Point of Sale sales and transaction data.',
            icon: 'fas fa-cash-register',
            status: 'unknown'
          },
          {
            key: 'boxme',
            name: 'BoxMe',
            description: 'BoxMe fulfillment and logistics integration.',
            icon: 'fas fa-box',
            status: 'connected'
          },
          {
            key: 'netsuite',
            name: 'NetSuite',
            description: 'NetSuite ERP accounting and inventory sync.',
            icon: 'fas fa-cloud',
            status: 'connected'
          },
          {
            key: 'omisell',
            name: 'Omisell',
            description: 'Omisell marketplace and order management.',
            icon: 'fas fa-store',
            status: 'unknown'
          }
        ]
      };
    },
    watch: {
      // Lazy-load BoxMe logs the first time the tab is opened, and manage
      // the BoxMe-only auto-reload timer as the user switches tabs.
      activeTab(tab) {
        if (tab === 'boxme') {
          if (!this.boxme.loaded) {
            this.fetchBoxmeLogs();
          }
          this.startAutoReload();
        } else {
          this.stopAutoReload();
        }
        if (tab === 'lynexus') {
          this.fetchHeartbeat();
          this.startHeartbeatAutoRefresh();
        } else {
          this.stopHeartbeatAutoRefresh();
        }
        // Load sync flows when entering a tab that shows them
        if ((tab === 'boxme' || tab === 'netsuite') && this.syncFlows.items.length === 0) {
          this.fetchSyncFlows();
        }
        // POS reconciliation: lazy-load on first entry
        if (tab === 'pos' && !this.posRecon.loaded) {
          this.fetchPosReconciliation();
        }
        // Omisell logs: lazy-load on first entry
        if (tab === 'omisell' && !this.omisell.loaded) {
          this.fetchOmisellLogs();
        }
        // NetSuite documents: load on first entry so the row shows its record count
        if (tab === 'netsuite' && !this.netsuite.loaded) {
          this.fetchNetsuiteLogs();
        }
      }
    },
    created() {
      // Let users resize / drag-reorder the columns of the document log tables.
      // Restore any previously saved layout (order + widths) first, then wire
      // up the handlers that persist future changes.
      ['boxmeColumns', 'netsuiteColumns', 'omisellColumns'].forEach(listName => {
        this.applySavedColumnLayout(listName);
        this.makeColumnsAdjustable(listName);
      });

      // Keep isFullscreen in sync when the user leaves browser fullscreen with Esc
      document.addEventListener('fullscreenchange', this.onFullscreenChange);
      document.addEventListener('webkitfullscreenchange', this.onFullscreenChange);
    },
    mounted() {
      if (this.loggedIn == false && this.developerMode == false) {
        this.$router.push('/')
      }
      // If BoxMe is the active tab on load, fetch immediately and start auto-reload
      if (this.activeTab === 'boxme') {
        this.fetchBoxmeLogs();
        this.startAutoReload();
        this.fetchSyncFlows();
      }
      // If Lynexus is the active tab on load, fetch heartbeats and start auto-refresh
      if (this.activeTab === 'lynexus') {
        this.fetchHeartbeat();
        this.startHeartbeatAutoRefresh();
      }
      // If POS is the active tab on load, fetch the reconciliation table
      if (this.activeTab === 'pos') {
        this.fetchPosReconciliation();
      }
      // If Omisell is the active tab on load, fetch its logs
      if (this.activeTab === 'omisell') {
        this.fetchOmisellLogs();
      }
      // If NetSuite is the active tab on load, fetch its document logs
      if (this.activeTab === 'netsuite') {
        this.fetchNetsuiteLogs();
      }
      // "Last updated X ago" clock (cheap; only meaningful once BoxMe has loaded)
      this.clockTimer = setInterval(this.updateLastRefreshedLabel, 15000);
    },
    beforeDestroy() {
      document.removeEventListener('fullscreenchange', this.onFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', this.onFullscreenChange);
      // Don't leave the browser stuck in fullscreen after navigating away
      if (this.isFullscreen) {
        this.exitFullscreen();
      }
      this.stopAutoReload();
      this.stopHeartbeatAutoRefresh();
      clearInterval(this.clockTimer);
      clearTimeout(this.boxmeSearchTimer);
      clearTimeout(this.omisellSearchTimer);
      clearTimeout(this.netsuiteSearchTimer);
      clearTimeout(this.posReconFilterTimer);
    },
    methods: {
      // ---- BoxMe auto reload ----

      // (Re)start the BoxMe reload timer using the currently selected minutes
      startAutoReload() {
        this.stopAutoReload();
        const ms = this.autoReloadMinutes * 60 * 1000;
        this.autoReloadTimer = setInterval(() => {
          // Only reload while the BoxMe tab is active
          if (this.activeTab === 'boxme') {
            this.fetchBoxmeLogs();
          }
        }, ms);
      },

      stopAutoReload() {
        if (this.autoReloadTimer) {
          clearInterval(this.autoReloadTimer);
          this.autoReloadTimer = null;
        }
      },

      // Called when the user changes the interval dropdown — restart with new interval
      onAutoReloadChange() {
        this.startAutoReload();
      },

      // Human-friendly "updated X ago" label
      updateLastRefreshedLabel() {
        this.lastRefreshedLabel = this.lastRefreshedAt
          ? moment(this.lastRefreshedAt).fromNow()
          : '';
      },

      statusLabel(status) {
        const labels = {
          connected: 'No Issue',
          warning: 'Needs Attention',
          disconnected: 'Disconnected',
          unknown: 'No Data'
        };
        return labels[status] || 'Unknown';
      },

      // ---- Lynexus heartbeat ----

      // (Re)start the heartbeat auto-refresh timer using the selected minutes
      startHeartbeatAutoRefresh() {
        this.stopHeartbeatAutoRefresh();
        const ms = this.heartbeat.autoRefreshMinutes * 60 * 1000;
        this.heartbeat.autoRefreshTimer = setInterval(() => {
          // Only refresh while the Lynexus tab is active
          if (this.activeTab === 'lynexus') {
            this.fetchHeartbeat();
          }
        }, ms);
      },

      stopHeartbeatAutoRefresh() {
        if (this.heartbeat.autoRefreshTimer) {
          clearInterval(this.heartbeat.autoRefreshTimer);
          this.heartbeat.autoRefreshTimer = null;
        }
      },

      // Called when the user changes the interval dropdown — restart with new interval
      onHeartbeatAutoRefreshChange() {
        this.startHeartbeatAutoRefresh();
      },

      heartbeatStatusLabel(status) {
        const labels = {
          up: 'Online',
          down: 'Offline',
          warning: 'Degraded',
          unknown: 'Unknown'
        };
        return labels[status] || 'Unknown';
      },

      // Icon per health state — reinforces status beyond colour (accessibility).
      heartbeatStatusIcon(status) {
        const icons = {
          up: 'fas fa-circle-check',
          down: 'fas fa-circle-exclamation',
          warning: 'fas fa-triangle-exclamation',
          unknown: 'fas fa-circle-question'
        };
        return icons[status] || icons.unknown;
      },

      // Format a timestamp for the heartbeat cards
      formatTime(value) {
        if (!value) return '—';
        const m = moment(value);
        return m.isValid() ? m.format('MMM DD, HH:mm:ss') : String(value);
      },

      // Pill class for a sync-flow record status (SUCCESS / PENDING / ERROR)
      recordStatusClass(status) {
        const s = String(status).toUpperCase();
        if (s === 'ERROR') return 'log-status-error';
        if (s === 'PENDING') return 'log-status-pending';
        return 'log-status-success';
      },

      // Columns for a flow's records table. Sites uses a "Code" column
      // (refcode); B2B Customers uses a "Code" column (code); other flows
      // use "Barcode".
      recordColumnsFor(flow) {
        // POS Sales (BXI -> NetSuite) has sales-specific columns.
        if (flow && flow.key === 'netsuite_pos_sales') {
          return [
            { title: 'Z-Reading Id', dataIndex: 'id', width: 120 },
            { title: 'Terminal', dataIndex: 'terminalCode', width: 140 },
            { title: 'Site', dataIndex: 'siteName', width: 260 },
            { title: 'Business Date', dataIndex: 'businessDate', width: 140 },
            { title: 'Sales', dataIndex: 'salesLabel', width: 140, align: 'right' },
            { title: 'Txns', dataIndex: 'txns', width: 90, align: 'right' },
            { title: 'Channels', dataIndex: 'channels', width: 100, align: 'right' },
            { title: 'Status', dataIndex: 'status', width: 110, scopedSlots: { customRender: 'status' } }
          ];
        }

        let identifier = { title: 'Barcode', dataIndex: 'barcode', width: 200 };
        if (flow && (flow.key === 'boxme_sites' || flow.key === 'netsuite_sites')) {
          identifier = { title: 'Code', dataIndex: 'refcode', width: 200 };
        } else if (flow && (flow.key === 'boxme_b2b_customers' || flow.key === 'netsuite_b2b_customers')) {
          identifier = { title: 'Code', dataIndex: 'code', width: 200 };
        }
        return [
          { title: 'Id', dataIndex: 'id', width: 140 },
          identifier,
          { title: 'Name', dataIndex: 'name', width: 500 },
          { title: 'Status', dataIndex: 'status', width: 110, scopedSlots: { customRender: 'status' } },
          { title: 'Synced At', dataIndex: 'syncedAt', width: 170 },
          { title: 'Message', dataIndex: 'message', scopedSlots: { customRender: 'message' } }
        ];
      },

      // Map a snake_case heartbeat row to the camelCase keys the cards use
      mapHeartbeat(row) {
        return {
          key: row.key,
          name: row.name,
          icon: row.icon || 'fas fa-plug',
          status: row.status || 'unknown',
          processed: row.processed,
          startedAt: row.started_at,
          stoppedAt: row.stopped_at,
          lastErrorAt: row.last_error_at,
          lastErrorMessage: row.last_error_message || ''
        };
      },

      // Fetch the heartbeat/health of each 3rd-party integration
      async fetchHeartbeat() {
        this.heartbeat.loading = true;

        const response = await HTTPRequest.getRequest(this.endPoint + 'integrations/heartbeat', null);

        if (response && !response.error && Array.isArray(response.integrations)) {
          this.heartbeat.items = response.integrations.map(this.mapHeartbeat);
        } else {
          this.$message && this.$message.error((response && response.error) || 'Failed to load integration heartbeat');
        }

        this.heartbeat.lastCheckedAt = new Date();
        this.updateHeartbeatLabel();
        this.heartbeat.loading = false;
      },

      updateHeartbeatLabel() {
        this.heartbeat.lastCheckedLabel = this.heartbeat.lastCheckedAt
          ? moment(this.heartbeat.lastCheckedAt).fromNow()
          : '';
      },

      // ---- Heartbeat history table ----

      // Fetch the heartbeat history applying the current filters + page
      async fetchHeartbeatHistory() {
        const h = this.heartbeatHistory;
        h.loading = true;

        const params = {
          page: h.pagination.current,
          per_page: h.pagination.pageSize
        };
        const range = h.dateRange;
        if (range && range.length === 2 && range[0] && range[1]) {
          params.date_from = moment(range[0]).format('YYYY-MM-DD');
          params.date_to = moment(range[1]).format('YYYY-MM-DD');
        }
        if (h.integrationKey) params.integration_key = h.integrationKey;
        if (h.status) params.status = h.status;

        const response = await HTTPRequest.getRequest(this.endPoint + 'integrations/heartbeat_history', params);

        if (response && !response.error && Array.isArray(response.heartbeats)) {
          h.rows = response.heartbeats.map(row => ({
            id: row.id,
            name: row.name,
            status: row.status,
            processed: row.processed != null ? row.processed : '—',
            recordedAt: row.recorded_at ? this.formatTime(row.recorded_at) : '—',
            startedAt: row.started_at ? this.formatTime(row.started_at) : '—',
            stoppedAt: row.stopped_at ? this.formatTime(row.stopped_at) : '—',
            lastErrorMessage: row.last_error_message || ''
          }));
          h.total = response.total || 0;
          h.pagination.total = h.total;
          h.pagination.current = response.page || h.pagination.current;
        } else {
          h.rows = [];
          h.total = 0;
          h.pagination.total = 0;
          this.$message && this.$message.error((response && response.error) || 'Failed to load heartbeat history');
        }

        h.loaded = true;
        h.loading = false;
      },

      // Collapse / expand the history section; load on first expand.
      toggleHeartbeatHistory() {
        this.heartbeatHistory.collapsed = !this.heartbeatHistory.collapsed;
        if (!this.heartbeatHistory.collapsed && !this.heartbeatHistory.loaded) {
          this.fetchHeartbeatHistory();
        }
      },

      // Filter change: reset to page 1 and refetch
      onHeartbeatHistoryFilter() {
        this.heartbeatHistory.pagination.current = 1;
        this.fetchHeartbeatHistory();
      },

      onHeartbeatHistoryTableChange(pagination) {
        this.heartbeatHistory.pagination.current = pagination.current;
        this.heartbeatHistory.pagination.pageSize = pagination.pageSize;
        this.fetchHeartbeatHistory();
      },

      // Open the graph modal and build the chart from the filtered history.
      async openHeartbeatGraph() {
        const g = this.heartbeatGraph;
        g.visible = true;
        g.loading = true;

        // Pull a larger window (same filters) so the chart is meaningful.
        const h = this.heartbeatHistory;
        const params = { page: 1, per_page: 100 };
        const range = h.dateRange;
        if (range && range.length === 2 && range[0] && range[1]) {
          params.date_from = moment(range[0]).format('YYYY-MM-DD');
          params.date_to = moment(range[1]).format('YYYY-MM-DD');
        }
        if (h.integrationKey) params.integration_key = h.integrationKey;
        if (h.status) params.status = h.status;

        const response = await HTTPRequest.getRequest(this.endPoint + 'integrations/heartbeat_history', params);

        g.rawRows = (response && !response.error && Array.isArray(response.heartbeats))
          ? response.heartbeats
          : [];
        this.buildHeartbeatGraph();

        g.loading = false;
      },

      // (Re)build the chart from the already-fetched rows for the active mode.
      buildHeartbeatGraph() {
        const g = this.heartbeatGraph;
        const rows = g.rawRows;
        if (!rows.length) {
          g.chartData = { labels: [], datasets: [] };
          g.hasData = false;
          return;
        }

        const palette = { boxme: '#5e72e4', netsuite: '#2dce89', omisell: '#fb6340' };

        if (g.mode === 'processed') {
          // Line: processed over time, one line per integration (oldest -> newest).
          const ordered = rows.slice().reverse();
          const labels = ordered.map(r => this.formatTime(r.recorded_at));
          const byKey = {};
          ordered.forEach((r, idx) => {
            if (!byKey[r.key]) {
              byKey[r.key] = {
                label: r.name,
                borderColor: palette[r.key] || '#8898aa',
                backgroundColor: 'transparent',
                borderWidth: 2,
                pointRadius: 3,
                lineTension: 0.3,
                data: new Array(ordered.length).fill(null)
              };
            }
            byKey[r.key].data[idx] = r.processed != null ? r.processed : 0;
          });
          g.chartData = { labels: labels, datasets: Object.values(byKey) };
          g.hasData = Object.keys(byKey).length > 0;
          return;
        }

        // Status counts (grouped bar): x = integrations, series = status.
        const order = ['boxme', 'netsuite', 'omisell'];
        const nameByKey = {};
        rows.forEach(r => { nameByKey[r.key] = r.name; });
        const keys = order.filter(k => nameByKey[k])
          .concat(Object.keys(nameByKey).filter(k => !order.includes(k)));
        const labels = keys.map(k => nameByKey[k]);

        const statuses = [
          { key: 'up', label: 'Online', color: '#2dce89' },
          { key: 'warning', label: 'Degraded', color: '#fb6340' },
          { key: 'down', label: 'Offline', color: '#f5365c' },
          { key: 'unknown', label: 'Unknown', color: '#ced4da' }
        ];
        const datasets = statuses.map(s => ({
          label: s.label,
          backgroundColor: s.color,
          data: keys.map(k => rows.filter(r => r.key === k && r.status === s.key).length)
        })).filter(ds => ds.data.some(v => v > 0));

        g.chartData = { labels: labels, datasets: datasets };
        g.hasData = datasets.length > 0;
      },

      // ---- POS reconciliation ----

      async fetchPosReconciliation() {
        const p = this.posRecon;
        if (p.loading) return; // prevent concurrent calls
        p.loading = true;

        const params = {
          page: p.pagination.current,
          per_page: p.pagination.pageSize
        };
        const range = p.businessDate;
        if (range && range.length === 2 && range[0] && range[1]) {
          params.date_from = moment(range[0]).format('YYYY-MM-DD');
          params.date_to = moment(range[1]).format('YYYY-MM-DD');
        }
        if (p.status) params.status = p.status;
        if (p.site && p.site.trim()) params.site = p.site.trim();
        if (p.terminal && p.terminal.trim()) params.terminal = p.terminal.trim();

        const response = await HTTPRequest.getRequest(this.endPoint + 'integrations/pos_reconciliation', params);

        if (response && !response.error && Array.isArray(response.records)) {
          p.rows = response.records;
          p.pagination.total = response.total || 0;
          p.pagination.current = response.page || p.pagination.current;
          p.pendingCount = response.pending_count || 0;
          p.errorCount = response.error_count || 0;
          p.successCount = response.success_count || 0;
        } else {
          p.rows = [];
          p.pagination.total = 0;
          p.successCount = 0;
          p.pendingCount = 0;
          p.errorCount = 0;
          this.$message && this.$message.error((response && response.error) || 'Failed to load POS reconciliation');
        }

        p.loaded = true;
        p.loading = false;
      },

      onPosReconFilter() {
        this.posRecon.pagination.current = 1;
        this.fetchPosReconciliation();
      },

      // Debounced for the free-text Site / Terminal inputs.
      // Only triggers when each field has ≥5 characters or is empty (cleared).
      onPosReconFilterDebounced() {
        clearTimeout(this.posReconFilterTimer);
        const siteLen = (this.posRecon.site || '').trim().length;
        const termLen = (this.posRecon.terminal || '').trim().length;
        const siteOk = siteLen === 0 || siteLen >= 5;
        const termOk = termLen === 0 || termLen >= 5;
        if (!siteOk || !termOk) return;
        this.posReconFilterTimer = setTimeout(() => {
          this.onPosReconFilter();
        }, 400);
      },

      onPosReconTableChange(pagination) {
        this.posRecon.pagination.current = pagination.current;
        this.posRecon.pagination.pageSize = pagination.pageSize;
        this.fetchPosReconciliation();
      },

      // ---- Sync flows ----

      // Map a snake_case flow SUMMARY row to the camelCase keys the UI uses.
      // Records are NOT included here — they are lazy-loaded on expand.
      mapSyncFlow(row) {
        // POS Sales defaults to the last 1 day; other flows to the last 3 days.
        const defaultDays = row.key === 'netsuite_pos_sales' ? 1 : 3;
        return {
          key: row.key,
          integration: row.integration,
          source: row.source,
          target: row.target,
          entity: row.entity,
          direction: row.direction,
          status: row.status || 'unknown',
          successCount: row.success_count != null ? row.success_count : 0,
          errorCount: row.error_count != null ? row.error_count : 0,
          pendingCount: row.pending_count != null ? row.pending_count : 0,
          lastSyncedAt: row.last_synced_at,
          lastErrorAt: row.last_error_at,
          // Per-flow lazy-load state for the records table.
          recordsLoaded: false,
          recordsLoading: false,
          records: [],
          dateRange: [moment().subtract(defaultDays, 'days'), moment()],
          statusFilter: '',
          // POS Sales only: offline (physical POS) / online (ecom) / all.
          // Defaults to all to match the backend default.
          posMode: 'all',
          exporting: false,
          recordsPagination: { current: 1, pageSize: 5, total: 0, showSizeChanger: true }
        };
      },

      async fetchSyncFlows() {
        this.syncFlows.loading = true;
        const response = await HTTPRequest.getRequest(this.endPoint + 'integrations/sync_flows', null);
        if (response && !response.error && Array.isArray(response.flows)) {
          this.syncFlows.items = response.flows.map(this.mapSyncFlow);
        } else {
          this.$message && this.$message.error((response && response.error) || 'Failed to load sync flows');
        }
        this.syncFlows.loading = false;
      },

      // Build the sync_flow_records query params from a flow's filters
      flowRecordParams(flow, extra) {
        const params = { flow: flow.key };
        const range = flow.dateRange;
        if (range && range.length === 2 && range[0] && range[1]) {
          params.date_from = moment(range[0]).format('YYYY-MM-DD');
          params.date_to = moment(range[1]).format('YYYY-MM-DD');
        }
        if (flow.statusFilter) {
          params.status = flow.statusFilter;
        }
        // POS Sales mode filter (offline / online / all)
        if (flow.key === 'netsuite_pos_sales' && flow.posMode) {
          params.pos_mode = flow.posMode;
        }
        return Object.assign(params, extra || {});
      },

      // Lazy-load records for a single flow (respects its date filter + page)
      async fetchFlowRecords(flow) {
        flow.recordsLoading = true;

        const params = this.flowRecordParams(flow, {
          page: flow.recordsPagination.current,
          per_page: flow.recordsPagination.pageSize
        });

        const response = await HTTPRequest.getRequest(this.endPoint + 'integrations/sync_flow_records', params);

        if (response && !response.error && Array.isArray(response.records)) {
          flow.records = response.records.map(rec => ({
            id: rec.id,
            barcode: rec.barcode || '',
            refcode: rec.refcode || '',
            code: rec.code || '',
            name: rec.name,
            // POS Sales (netsuite_pos_sales) extra fields
            terminalCode: rec.terminal_code || '',
            siteName: rec.site_name || '',
            businessDate: rec.business_date || '',
            sales: rec.sales != null ? Number(rec.sales) : null,
            salesLabel: rec.sales != null
              ? Number(rec.sales).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
              : '',
            txns: rec.txns != null ? rec.txns : '',
            channels: rec.channels != null ? rec.channels : '',
            status: rec.status,
            // Pending records haven't synced yet, so show no synced-at time
            syncedAt: (String(rec.status).toUpperCase() === 'PENDING')
              ? ''
              : (rec.synced_at ? this.formatTime(rec.synced_at) : '—'),
            message: rec.message || ''
          }));
          flow.recordsPagination.total = response.total || 0;
          flow.recordsPagination.current = response.page || flow.recordsPagination.current;
          // Keep the flow row's badges in sync with the active date window
          if (response.success_count != null) flow.successCount = response.success_count;
          if (response.error_count != null) flow.errorCount = response.error_count;
          if (response.pending_count != null) flow.pendingCount = response.pending_count;
        } else {
          flow.records = [];
          flow.recordsPagination.total = 0;
          this.$message && this.$message.error((response && response.error) || 'Failed to load flow records');
        }

        flow.recordsLoaded = true;
        flow.recordsLoading = false;
      },

      // Export all records matching the flow's current filters to CSV
      async exportFlowCsv(flow) {
        flow.exporting = true;

        // Pull a large page so the export covers the whole filtered set
        const params = this.flowRecordParams(flow, { page: 1, per_page: 100 });
        const response = await HTTPRequest.getRequest(this.endPoint + 'integrations/sync_flow_records', params);

        if (!response || response.error || !Array.isArray(response.records)) {
          this.$message && this.$message.error((response && response.error) || 'Export failed');
          flow.exporting = false;
          return;
        }

        let lines;
        if (flow.key === 'netsuite_pos_sales') {
          // POS Sales (BXI -> NetSuite): sales-specific columns.
          const headers = ['Z-Reading Id', 'Terminal', 'Site', 'Business Date', 'Sales', 'Txns', 'Channels', 'Status'];
          lines = [headers.map(this.csvCell).join(',')];
          response.records.forEach(rec => {
            lines.push([
              rec.id, rec.terminal_code || '', rec.site_name || '',
              rec.business_date || '',
              rec.sales != null ? Number(rec.sales).toFixed(2) : '',
              rec.txns != null ? rec.txns : '',
              rec.channels != null ? rec.channels : '',
              rec.status
            ].map(this.csvCell).join(','));
          });
        } else {
          const isSites = flow.key === 'boxme_sites' || flow.key === 'netsuite_sites';
          const isB2bCustomers = flow.key === 'boxme_b2b_customers' || flow.key === 'netsuite_b2b_customers';
          const usesCode = isSites || isB2bCustomers;
          const idLabel = usesCode ? 'Code' : 'Barcode';
          const headers = ['Id', idLabel, 'Name', 'Status', 'Synced At', 'Message'];
          lines = [headers.map(this.csvCell).join(',')];
          response.records.forEach(rec => {
            const isPending = String(rec.status).toUpperCase() === 'PENDING';
            let identifier = rec.barcode || '';
            if (isSites) {
              identifier = rec.refcode || '';
            } else if (isB2bCustomers) {
              identifier = rec.code || '';
            }
            lines.push([
              rec.id, identifier, rec.name, rec.status,
              isPending ? '' : (rec.synced_at ? this.formatTime(rec.synced_at) : ''),
              rec.message || ''
            ].map(this.csvCell).join(','));
          });
        }

        const csv = '\uFEFF' + lines.join('\r\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        const stamp = moment().format('YYYYMMDD_HHmmss');

        link.href = url;
        link.setAttribute('download', `${flow.key}_records_${stamp}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        flow.exporting = false;
      },

      // Date-range change on a flow: reset to page 1 and refetch
      onFlowDateChange(flow) {
        flow.recordsPagination.current = 1;
        this.fetchFlowRecords(flow);
      },

      // Pagination / page-size change on a flow's records table
      onFlowRecordsTableChange(flow, pagination) {
        flow.recordsPagination.current = pagination.current;
        flow.recordsPagination.pageSize = pagination.pageSize;
        this.fetchFlowRecords(flow);
      },

      // Flows for one integration, grouped by direction (source → target)
      groupedFlows(integrationKey) {
        const flows = this.syncFlows.items.filter(f => f.integration === integrationKey);
        const groups = [];
        flows.forEach(f => {
          const label = `${f.source} → ${f.target}`;
          let group = groups.find(g => g.label === label);
          if (!group) {
            group = { label, flows: [] };
            groups.push(group);
          }
          group.flows.push(f);
        });
        return groups;
      },

      toggleFlow(key) {
        // Accordion: clicking an open row closes it; clicking a closed row
        // opens it and auto-closes any other expanded row.
        if (this.expandedFlows.includes(key)) {
          this.expandedFlows = [];
          return;
        }
        this.expandedFlows = [key];

        // The Documents row is backed by the BoxMe logs endpoint, not a sync
        // flow — load it on first expand.
        if (key === 'boxme_logs') {
          if (!this.boxme.loaded && !this.boxme.loading) {
            this.fetchBoxmeLogs();
          }
          return;
        }
        // Same for the NetSuite Documents row (document_external_logs)
        if (key === 'netsuite_logs') {
          if (!this.netsuite.loaded && !this.netsuite.loading) {
            this.fetchNetsuiteLogs();
          }
          return;
        }
        // Lazy-load this flow's records the first time it is expanded
        const flow = this.syncFlows.items.find(f => f.key === key);
        if (flow && !flow.recordsLoaded && !flow.recordsLoading) {
          this.fetchFlowRecords(flow);
        }
      },

      // Row count for a direction group. The BoxMe "BXI → BoxMe" group has an
      // extra static "Documents" row that is not part of group.flows.
      flowGroupCount(integrationKey, group) {
        let count = group.flows.length;
        if (integrationKey === 'boxme' && group.label === 'BXI → BoxMe') {
          count += 1;
        }
        if (integrationKey === 'netsuite' && group.label === 'BXI → NetSuite') {
          count += 1;
        }
        return count;
      },

      // Collapse / expand a whole direction group (e.g. "BXI → BoxMe")
      toggleFlowGroup(label) {
        const idx = this.collapsedGroups.indexOf(label);
        if (idx === -1) {
          this.collapsedGroups.push(label);
        } else {
          this.collapsedGroups.splice(idx, 1);
        }
      },

      // Map a snake_case API row to the camelCase keys the table columns use
      mapBoxmeRow(row) {
        return {
          id: row.id,
          date: row.date,
          time: row.time,
          status: row.status,
          documentType: row.document_type,
          documentNumber: row.document_number,
          referenceCode: row.reference_code,
          casNo: row.cas_no,
          orderSource: row.order_source,
          sbu: row.sbu,
          requestId: row.request_id,
          error: row.error,
          message: row.message
        };
      },

      // Omisell rows carry the same fields as BoxMe plus print_status
      // (derived from documents.reference_6: 0 PENDING, 1 PRINTED, 2 ERROR)
      mapOmisellRow(row) {
        return Object.assign(this.mapBoxmeRow(row), {
          printStatus: row.print_status
        });
      },

      // Build the query params sent to the backend from the current filters
      boxmeQueryParams(extra) {
        const { search, status, documentType, dateRange } = this.boxme.filters;
        const params = {};
        if (search && search.trim()) params.search = search.trim();
        if (status) params.status = status;
        if (documentType) params.document_type = documentType;
        if (dateRange && dateRange.length === 2 && dateRange[0] && dateRange[1]) {
          params.date_from = moment(dateRange[0]).format('YYYY-MM-DD');
          params.date_to = moment(dateRange[1]).format('YYYY-MM-DD');
        }
        return Object.assign(params, extra || {});
      },

      // Fetch a page of BoxMe logs from the backend
      async fetchBoxmeLogs() {
        this.boxme.loading = true;
        const params = this.boxmeQueryParams({
          page: this.boxme.pagination.current,
          per_page: this.boxme.pagination.pageSize
        });

        const response = await HTTPRequest.getRequest(this.endPoint + 'integrations/boxme_logs', params);

        if (response && !response.error) {
          const rows = (response.boxme_logs || []).map(this.mapBoxmeRow);
          this.boxme.rows = rows;
          this.boxme.total = response.total || 0;
          this.boxme.pagination.total = response.total || 0;
          this.boxme.pagination.current = response.page || this.boxme.pagination.current;
          // Collect document types seen so far for the dropdown
          const types = new Set(this.boxme.documentTypes);
          rows.forEach(r => { if (r.documentType) types.add(r.documentType); });
          this.boxme.documentTypes = [...types].sort();
          // Reflect data health in the BoxMe status badge: if any row has an
          // ERROR status, flag the integration as needing attention.
          this.updateBoxmeStatus(rows);
          // Record refresh time for the auto-reload "updated X ago" label
          this.lastRefreshedAt = new Date();
          this.updateLastRefreshedLabel();
        } else {
          this.boxme.rows = [];
          this.boxme.total = 0;
          this.boxme.pagination.total = 0;
          // Could not reach the endpoint: mark the integration disconnected
          this.setIntegrationStatus('boxme', 'disconnected');
          this.$message && this.$message.error((response && response.error) || 'Failed to load BoxMe logs');
        }

        this.boxme.loaded = true;
        this.boxme.loading = false;
      },

      // Set an integration's status badge by key (reactive-safe via splice)
      setIntegrationStatus(key, status) {
        const idx = this.integrations.findIndex(i => i.key === key);
        if (idx !== -1 && this.integrations[idx].status !== status) {
          this.integrations.splice(idx, 1, { ...this.integrations[idx], status });
        }
      },

      // Derive the BoxMe status from the fetched rows:
      //  - any ERROR row  -> 'warning' (Needs Attention)
      //  - rows, no error -> 'connected'
      //  - no rows        -> 'unknown' (Not Configured)
      updateBoxmeStatus(rows) {
        let status = 'unknown';
        if (rows.length) {
          status = rows.some(r => String(r.status).toUpperCase() === 'ERROR')
            ? 'warning'
            : 'connected';
        }
        this.setIntegrationStatus('boxme', status);
      },

      // Any filter change: reset to the first page and refetch
      applyBoxmeFilters() {
        this.boxme.pagination.current = 1;
        this.fetchBoxmeLogs();
      },

      // Debounce the free-text search; only trigger once the user has typed
      // at least 5 characters (or cleared the field back to empty).
      onBoxmeSearchInput() {
        clearTimeout(this.boxmeSearchTimer);
        const term = (this.boxme.filters.search || '').trim();
        if (term.length > 0 && term.length < 5) {
          return; // wait for at least 5 characters before searching
        }
        this.boxmeSearchTimer = setTimeout(() => {
          this.applyBoxmeFilters();
        }, 400);
      },

      // Table pagination / page-size change
      onBoxmeTableChange(pagination) {
        this.boxme.pagination.current = pagination.current;
        this.boxme.pagination.pageSize = pagination.pageSize;
        this.fetchBoxmeLogs();
      },

      resetBoxmeFilters() {
        this.boxme.filters = {
          search: '',
          status: '',
          documentType: '',
          // Reset back to the default last-3-days range
          dateRange: [moment().subtract(3, 'days'), moment()]
        };
        this.applyBoxmeFilters();
      },

      // Escape a value for safe inclusion in a CSV cell
      csvCell(value) {
        const str = value == null ? '' : String(value);
        if (/[",\n]/.test(str)) {
          return '"' + str.replace(/"/g, '""') + '"';
        }
        return str;
      },

      // Export ALL rows matching the current filters (fetched from backend) to CSV
      async exportBoxmeCsv() {
        this.boxme.exporting = true;

        // Request a single large page so the export covers every filtered row
        const params = this.boxmeQueryParams({ page: 1, per_page: 100 });
        const response = await HTTPRequest.getRequest(this.endPoint + 'integrations/boxme_logs', params);

        if (!response || response.error) {
          this.$message && this.$message.error((response && response.error) || 'Export failed');
          this.boxme.exporting = false;
          return;
        }

        const rows = (response.boxme_logs || []).map(this.mapBoxmeRow);
        const headers = this.boxmeColumns.map(c => c.title);
        const keys = this.boxmeColumns.map(c => c.dataIndex);

        const lines = [headers.map(this.csvCell).join(',')];
        rows.forEach(row => {
          lines.push(keys.map(k => this.csvCell(row[k])).join(','));
        });

        // Prepend BOM so Excel opens UTF-8 content correctly
        const csv = '\uFEFF' + lines.join('\r\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        const stamp = moment().format('YYYYMMDD_HHmmss');

        link.href = url;
        link.setAttribute('download', `boxme_logs_${stamp}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        this.boxme.exporting = false;
      },

      // ---- Full screen ----

      toggleFullscreen() {
        if (this.isFullscreen) {
          this.exitFullscreen();
        } else {
          this.enterFullscreen();
        }
      },

      // Maximize the dashboard over the app chrome (CSS), and also ask the
      // browser for real fullscreen on the whole page. The page element (not
      // just the dashboard) is used because Ant Design dropdowns / date pickers
      // render on <body> and would be hidden inside a fullscreen sub-element.
      // If the browser refuses fullscreen, the CSS-maximized view still works.
      enterFullscreen() {
        this.isFullscreen = true;
        const el = document.documentElement;
        const request = el.requestFullscreen || el.webkitRequestFullscreen;
        if (request) {
          try {
            const result = request.call(el);
            if (result && result.catch) {
              result.catch(() => {});
            }
          } catch (e) {
            // ignore: CSS-maximized view stays active
          }
        }
      },

      exitFullscreen() {
        this.isFullscreen = false;
        const active = document.fullscreenElement || document.webkitFullscreenElement;
        if (active) {
          const exit = document.exitFullscreen || document.webkitExitFullscreen;
          if (exit) {
            try {
              const result = exit.call(document);
              if (result && result.catch) {
                result.catch(() => {});
              }
            } catch (e) {
              // ignore
            }
          }
        }
      },

      // Browser left fullscreen (e.g. user pressed Esc): drop the maximized view too
      onFullscreenChange() {
        const active = document.fullscreenElement || document.webkitFullscreenElement;
        if (!active && this.isFullscreen) {
          this.isFullscreen = false;
        }
      },

      // ---- Adjustable table columns (resize + drag to reorder, persisted) ----

      // localStorage key for a column list's saved layout
      columnLayoutStorageKey(listName) {
        return `integrationDashboard.columnLayout.${listName}`;
      },

      // Re-order (and re-apply saved widths to) the columns of `listName`
      // using whatever layout was saved last time the user adjusted them.
      // Safe no-op if nothing was saved, or the saved data no longer matches
      // the current set of columns (e.g. after a column was added/removed).
      applySavedColumnLayout(listName) {
        let saved = null;
        try {
          const raw = window.localStorage.getItem(this.columnLayoutStorageKey(listName));
          saved = raw ? JSON.parse(raw) : null;
        } catch (e) {
          saved = null; // corrupt/unavailable storage: fall back to defaults
        }
        if (!Array.isArray(saved) || !saved.length) {
          return;
        }

        const current = this[listName];
        const byKey = {};
        current.forEach(col => { byKey[col.dataIndex] = col; });

        // Only trust the saved layout if it lists exactly the same columns
        // we have today; otherwise keep the current (code-defined) layout.
        const savedKeys = saved.map(s => s.dataIndex).sort().join('|');
        const currentKeys = current.map(c => c.dataIndex).sort().join('|');
        if (savedKeys !== currentKeys) {
          return;
        }

        this[listName] = saved.map(s => Object.assign({}, byKey[s.dataIndex], {
          width: s.width || byKey[s.dataIndex].width
        }));
      },

      // Persist the current order + widths of `listName` so it survives reloads
      saveColumnLayout(listName) {
        try {
          const layout = this[listName].map(c => ({ dataIndex: c.dataIndex, width: c.width }));
          window.localStorage.setItem(this.columnLayoutStorageKey(listName), JSON.stringify(layout));
        } catch (e) {
          // localStorage unavailable/full: silently skip persistence
        }
      },

      // Attach the header-cell hooks to every column of a column list (by data
      // property name). Columns are keyed by dataIndex.
      makeColumnsAdjustable(listName) {
        this[listName] = this[listName].map(col => Object.assign({}, col, {
          customHeaderCell: () => ({
            attrs: { 'data-colkey': col.dataIndex },
            on: {
              resize: (width, persist) => this.resizeColumn(listName, col.dataIndex, width, persist),
              reorder: (from, to) => this.reorderColumns(listName, from, to)
            }
          })
        }));
      },

      resizeColumn(listName, colKey, width, persist) {
        const col = this[listName].find(c => c.dataIndex === colKey);
        if (col) {
          this.$set(col, 'width', width);
          // Only write to localStorage once, when the drag ends (mouseup)
          if (persist) {
            this.saveColumnLayout(listName);
          }
        }
      },

      // Move the `fromKey` column to the position of the `toKey` column
      reorderColumns(listName, fromKey, toKey) {
        const cols = this[listName].slice();
        const from = cols.findIndex(c => c.dataIndex === fromKey);
        const to = cols.findIndex(c => c.dataIndex === toKey);
        if (from === -1 || to === -1 || from === to) {
          return;
        }
        cols.splice(to, 0, cols.splice(from, 1)[0]);
        this[listName] = cols;
        this.saveColumnLayout(listName);
      },

      // ---- Omisell logs (same concept as BoxMe logs) ----

      // Build the query params sent to the backend from the current Omisell filters
      omisellQueryParams(extra) {
        const { search, status, printStatus, documentType, dateRange } = this.omisell.filters;
        const params = {};
        if (search && search.trim()) params.search = search.trim();
        if (status) params.status = status;
        if (printStatus) params.print_status = printStatus;
        if (documentType) params.document_type = documentType;
        if (dateRange && dateRange.length === 2 && dateRange[0] && dateRange[1]) {
          params.date_from = moment(dateRange[0]).format('YYYY-MM-DD');
          params.date_to = moment(dateRange[1]).format('YYYY-MM-DD');
        }
        return Object.assign(params, extra || {});
      },

      // Fetch a page of Omisell logs from the backend
      async fetchOmisellLogs() {
        this.omisell.loading = true;
        const params = this.omisellQueryParams({
          page: this.omisell.pagination.current,
          per_page: this.omisell.pagination.pageSize
        });

        const response = await HTTPRequest.getRequest(this.endPoint + 'integrations/omisell_logs', params);

        if (response && !response.error) {
          const rows = (response.omisell_logs || []).map(this.mapOmisellRow);
          this.omisell.rows = rows;
          this.omisell.total = response.total || 0;
          this.omisell.pagination.total = response.total || 0;
          this.omisell.pagination.current = response.page || this.omisell.pagination.current;
          // Collect document types seen so far for the dropdown
          const types = new Set(this.omisell.documentTypes);
          rows.forEach(r => { if (r.documentType) types.add(r.documentType); });
          this.omisell.documentTypes = [...types].sort();
          // Reflect data health in the Omisell status badge
          this.updateOmisellStatus(rows);
        } else {
          this.omisell.rows = [];
          this.omisell.total = 0;
          this.omisell.pagination.total = 0;
          // Could not reach the endpoint: mark the integration disconnected
          this.setIntegrationStatus('omisell', 'disconnected');
          this.$message && this.$message.error((response && response.error) || 'Failed to load Omisell logs');
        }

        this.omisell.loaded = true;
        this.omisell.loading = false;
      },

      // Derive the Omisell status badge from the fetched rows:
      //  - any ERROR row  -> 'warning' (Needs Attention)
      //  - rows, no error -> 'connected'
      //  - no rows        -> 'unknown' (Not Configured)
      updateOmisellStatus(rows) {
        let status = 'unknown';
        if (rows.length) {
          status = rows.some(r => String(r.status).toUpperCase() === 'ERROR')
            ? 'warning'
            : 'connected';
        }
        this.setIntegrationStatus('omisell', status);
      },

      // Any filter change: reset to the first page and refetch
      applyOmisellFilters() {
        this.omisell.pagination.current = 1;
        this.fetchOmisellLogs();
      },

      // Debounce the free-text search; only trigger once the user has typed
      // at least 5 characters (or cleared the field back to empty).
      onOmisellSearchInput() {
        clearTimeout(this.omisellSearchTimer);
        const term = (this.omisell.filters.search || '').trim();
        if (term.length > 0 && term.length < 5) {
          return; // wait for at least 5 characters before searching
        }
        this.omisellSearchTimer = setTimeout(() => {
          this.applyOmisellFilters();
        }, 400);
      },

      // Table pagination / page-size change
      onOmisellTableChange(pagination) {
        this.omisell.pagination.current = pagination.current;
        this.omisell.pagination.pageSize = pagination.pageSize;
        this.fetchOmisellLogs();
      },

      resetOmisellFilters() {
        this.omisell.filters = {
          search: '',
          status: '',
          printStatus: '',
          documentType: '',
          // Reset back to the default last-3-days range
          dateRange: [moment().subtract(3, 'days'), moment()]
        };
        this.applyOmisellFilters();
      },

      // Export ALL rows matching the current filters (fetched from backend) to CSV
      async exportOmisellCsv() {
        this.omisell.exporting = true;

        // Request a single large page so the export covers every filtered row
        const params = this.omisellQueryParams({ page: 1, per_page: 100 });
        const response = await HTTPRequest.getRequest(this.endPoint + 'integrations/omisell_logs', params);

        if (!response || response.error) {
          this.$message && this.$message.error((response && response.error) || 'Export failed');
          this.omisell.exporting = false;
          return;
        }

        const rows = (response.omisell_logs || []).map(this.mapOmisellRow);
        const headers = this.omisellColumns.map(c => c.title);
        const keys = this.omisellColumns.map(c => c.dataIndex);

        const lines = [headers.map(this.csvCell).join(',')];
        rows.forEach(row => {
          lines.push(keys.map(k => this.csvCell(row[k])).join(','));
        });

        // Prepend BOM so Excel opens UTF-8 content correctly
        const csv = '\uFEFF' + lines.join('\r\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        const stamp = moment().format('YYYYMMDD_HHmmss');

        link.href = url;
        link.setAttribute('download', `omisell_logs_${stamp}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        this.omisell.exporting = false;
      },

      // ---- NetSuite document logs (document_external_logs) ----

      // Build the query params sent to the backend from the current NetSuite filters
      netsuiteQueryParams(extra) {
        const { search, status, documentType, dateRange } = this.netsuite.filters;
        const params = {};
        if (search && search.trim()) params.search = search.trim();
        if (status) params.status = status;
        if (documentType) params.document_type = documentType;
        if (dateRange && dateRange.length === 2 && dateRange[0] && dateRange[1]) {
          params.date_from = moment(dateRange[0]).format('YYYY-MM-DD');
          params.date_to = moment(dateRange[1]).format('YYYY-MM-DD');
        }
        return Object.assign(params, extra || {});
      },

      // Fetch a page of NetSuite document logs from the backend
      async fetchNetsuiteLogs() {
        this.netsuite.loading = true;
        const params = this.netsuiteQueryParams({
          page: this.netsuite.pagination.current,
          per_page: this.netsuite.pagination.pageSize
        });

        const response = await HTTPRequest.getRequest(this.endPoint + 'integrations/netsuite_logs', params);

        if (response && !response.error) {
          // Same row shape as BoxMe (incl. casNo), so reuse the same mapper
          const rows = (response.netsuite_logs || []).map(this.mapBoxmeRow);
          this.netsuite.rows = rows;
          this.netsuite.total = response.total || 0;
          this.netsuite.pagination.total = response.total || 0;
          this.netsuite.pagination.current = response.page || this.netsuite.pagination.current;
          // Collect document types seen so far for the dropdown
          const types = new Set(this.netsuite.documentTypes);
          rows.forEach(r => { if (r.documentType) types.add(r.documentType); });
          this.netsuite.documentTypes = [...types].sort();
        } else {
          this.netsuite.rows = [];
          this.netsuite.total = 0;
          this.netsuite.pagination.total = 0;
          this.$message && this.$message.error((response && response.error) || 'Failed to load NetSuite documents');
        }

        this.netsuite.loaded = true;
        this.netsuite.loading = false;
      },

      // Any filter change: reset to the first page and refetch
      applyNetsuiteFilters() {
        this.netsuite.pagination.current = 1;
        this.fetchNetsuiteLogs();
      },

      // Debounce the free-text search; only trigger once the user has typed
      // at least 5 characters (or cleared the field back to empty).
      onNetsuiteSearchInput() {
        clearTimeout(this.netsuiteSearchTimer);
        const term = (this.netsuite.filters.search || '').trim();
        if (term.length > 0 && term.length < 5) {
          return; // wait for at least 5 characters before searching
        }
        this.netsuiteSearchTimer = setTimeout(() => {
          this.applyNetsuiteFilters();
        }, 400);
      },

      // Table pagination / page-size change
      onNetsuiteTableChange(pagination) {
        this.netsuite.pagination.current = pagination.current;
        this.netsuite.pagination.pageSize = pagination.pageSize;
        this.fetchNetsuiteLogs();
      },

      resetNetsuiteFilters() {
        this.netsuite.filters = {
          search: '',
          status: '',
          documentType: '',
          // Reset back to the default last-3-days range
          dateRange: [moment().subtract(3, 'days'), moment()]
        };
        this.applyNetsuiteFilters();
      },

      // Export rows matching the current filters (fetched from backend) to CSV
      async exportNetsuiteCsv() {
        this.netsuite.exporting = true;

        const params = this.netsuiteQueryParams({ page: 1, per_page: 100 });
        const response = await HTTPRequest.getRequest(this.endPoint + 'integrations/netsuite_logs', params);

        if (!response || response.error) {
          this.$message && this.$message.error((response && response.error) || 'Export failed');
          this.netsuite.exporting = false;
          return;
        }

        const rows = (response.netsuite_logs || []).map(this.mapBoxmeRow);
        const headers = this.netsuiteColumns.map(c => c.title);
        const keys = this.netsuiteColumns.map(c => c.dataIndex);

        const lines = [headers.map(this.csvCell).join(',')];
        rows.forEach(row => {
          lines.push(keys.map(k => this.csvCell(row[k])).join(','));
        });

        // Prepend BOM so Excel opens UTF-8 content correctly
        const csv = '\uFEFF' + lines.join('\r\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        const stamp = moment().format('YYYYMMDD_HHmmss');

        link.href = url;
        link.setAttribute('download', `netsuite_logs_${stamp}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        this.netsuite.exporting = false;
      }
    }
  };
</script>

<style lang='scss'>
  .integration-dashboard {
    // Keep the header pattern as a fixed-aspect banner so it doesn't
    // re-stretch when content height changes (e.g. expanding a row / table).
    // !important ensures this beats BaseHeader.vue's scoped .bg-pattern rule.
    .bg-pattern {
      background-size: 100% auto !important;
      background-position: top center !important;
      background-repeat: no-repeat !important;
    }

    .page-heading {
      .page-title {
        color: #fff;
        font-weight: 700;
        font-size: 1.75rem;
        margin-bottom: 0.25rem;
      }
      .page-subtitle {
        color: rgba(255, 255, 255, 0.8);
        font-size: 1rem;
        margin-bottom: 0;
      }
    }

    // Larger, more legible tab headers
    .integration-tabs {
      .ant-tabs-tab {
        font-size: 1rem;
        font-weight: 600;
        padding: 12px 20px;
      }
      .tab-label {
        display: inline-flex;
        align-items: center;
      }
      .tab-icon {
        margin-right: 8px;
        font-size: 1rem;
      }
    }

    .integration-panel {
      background: #fff;
      border-radius: 0.5rem;
      box-shadow: 0 0 2rem 0 rgba(136, 152, 170, 0.15);
      overflow: hidden;
    }

    .panel-header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 1rem;
      padding: 1.25rem 1.5rem;
      border-bottom: 1px solid #e9ecef;
      background: linear-gradient(180deg, #fbfcff 0%, #ffffff 100%);

      .panel-title {
        font-size: 1.3rem;
        font-weight: 700;
        margin-bottom: 0.25rem;
        color: #32325d;
        letter-spacing: -0.01em;
      }
      .panel-description {
        font-size: 0.9rem;
        color: #8898aa;
        margin-bottom: 0;
      }
    }

    // Status badge: quick visual cue for the support team
    .status-badge {
      display: inline-flex;
      align-items: center;
      white-space: nowrap;
      font-size: 0.8rem;
      font-weight: 700;
      padding: 0.45rem 0.85rem;
      border-radius: 999px;
      text-transform: uppercase;
      letter-spacing: 0.03em;
      border: 1px solid transparent;

      .status-dot {
        width: 0.6rem;
        height: 0.6rem;
        border-radius: 50%;
        margin-right: 0.45rem;
        background: currentColor;
      }

      &.status-connected {
        color: #1aae6f;
        background: rgba(45, 206, 137, 0.12);
        border-color: rgba(45, 206, 137, 0.35);
        .status-dot { animation: badge-dot-pulse 1.8s infinite; }
      }
      &.status-warning {
        color: #d63b12;
        background: rgba(251, 99, 64, 0.12);
        border-color: rgba(251, 99, 64, 0.4);
        .status-dot { animation: badge-dot-pulse 1.3s infinite; }
      }
      &.status-disconnected {
        color: #f5365c;
        background: rgba(245, 54, 92, 0.14);
        border-color: rgba(245, 54, 92, 0.45);
        .status-dot { animation: badge-dot-pulse 0.9s infinite; }
      }
      &.status-unknown {
        color: #8898aa;
        background: rgba(136, 152, 170, 0.12);
        border-color: rgba(136, 152, 170, 0.3);
      }
    }

    .panel-body {
      padding: 2rem 1.5rem;
    }

    // Integration Logs section embedded inside the Sync Flows panel
    .integration-logs-section {
      margin-top: 1.5rem;
      padding-top: 1.5rem;
      border-top: 1px solid #e9ecef;
    }

    // Collapsible section header (BoxMe logs table)
    .section-collapse-header {
      display: flex;
      align-items: center;
      gap: 0.6rem;
      cursor: pointer;
      user-select: none;
      padding: 0.6rem 0.75rem;
      margin-bottom: 1rem;
      border: 1px solid #e9ecef;
      border-radius: 0.375rem;
      background: #f6f9fc;
      transition: background 0.15s ease;

      &:hover { background: #eef2f7; }

      .section-collapse-icon {
        color: #8898aa;
        font-size: 0.8rem;
        width: 0.9rem;
        text-align: center;
      }
      .section-collapse-title {
        font-weight: 600;
        color: #32325d;
      }
      .section-collapse-count {
        margin-left: auto;
        font-size: 0.8rem;
        color: #8898aa;
      }
    }

    .empty-state {
      text-align: center;
      padding: 2rem 1rem;

      .empty-icon {
        font-size: 2.5rem;
        color: #ced4da;
        margin-bottom: 1rem;
      }
      .empty-title {
        font-size: 1.1rem;
        font-weight: 600;
        color: #525f7f;
        margin-bottom: 0.5rem;
      }
      .empty-text {
        font-size: 0.9rem;
        color: #8898aa;
        margin-bottom: 0;
      }
    }

    // Filter bar above the BoxMe table
    .filter-bar {
      display: flex;
      flex-wrap: wrap;
      align-items: flex-end;
      gap: 1rem;
      margin-bottom: 1rem;

      .filter-field {
        display: flex;
        flex-direction: column;
        min-width: 180px;
        flex: 1 1 180px;
      }
      .filter-label {
        font-size: 0.75rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.02em;
        color: #8898aa;
        margin-bottom: 0.35rem;
      }
      .filter-actions {
        display: flex;
        gap: 0.5rem;
        align-items: flex-end;
      }
    }

    .result-count {
      font-size: 0.8rem;
      color: #8898aa;
      margin-bottom: 0.75rem;
    }

    .boxme-meta {
      display: flex;
      align-items: center;
      gap: 0.4rem;
      font-size: 0.8rem;
      color: #8898aa;
      margin-bottom: 0.5rem;

      i {
        color: #2dce89;
      }
    }

    // ---- Sync flows panel ----
    .sync-flows-body {
      border-bottom: 1px solid #e9ecef;
    }
    .sync-flows-head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 1rem;

      .sync-flows-title {
        font-size: 1rem;
        font-weight: 700;
        color: #32325d;
        margin: 0;
      }
    }
    .sync-flows-empty {
      font-size: 0.85rem;
      color: #8898aa;
      padding: 1rem 0;
    }

    .flow-group {
      margin-bottom: 1.25rem;
      display: flex;
      flex-direction: column;

      // Keep the label on top, then the Documents row, then the normal flows.
      .flow-group-label { order: -2; }
      .flow-row-wrap--first { order: -1; }

      .flow-group-label {
        display: flex;
        align-items: center;
        font-size: 0.72rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: #8898aa;
        margin-bottom: 0.5rem;
        cursor: pointer;
        user-select: none;

        &:hover { color: #525f7f; }

        i { margin-right: 0.35rem; color: #5e72e4; }
        .flow-group-caret { color: #8898aa; width: 0.8rem; }
        .flow-group-count {
          margin-left: auto;
          font-weight: 600;
          text-transform: none;
          letter-spacing: normal;
          color: #adb5bd;
        }
      }
    }

    .flow-row-wrap {
      border: 1px solid #e9ecef;
      border-radius: 0.5rem;
      margin-bottom: 0.5rem;
      overflow: hidden;
    }

    .flow-row {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      padding: 0.7rem 0.95rem;
      cursor: pointer;
      transition: background 0.12s ease, box-shadow 0.12s ease;
      border-left: 3px solid transparent;

      &:hover {
        background: #f6f9fc;
        box-shadow: inset 0 0 0 1px rgba(94, 114, 228, 0.12);
      }

      // Draw attention to rows that have failures.
      &.flow-row--has-error {
        border-left-color: #f5365c;
        background: rgba(245, 54, 92, 0.03);
      }

      // Highlight the currently open row so context is clear.
      &.flow-row--expanded {
        background: #f6f9fc;
        border-left-color: #5e72e4;
      }

      .flow-caret {
        color: #adb5bd;
        font-size: 0.75rem;
        width: 0.8rem;
        text-align: center;
      }
      .flow-status-dot {
        width: 0.6rem;
        height: 0.6rem;
        border-radius: 50%;
        flex: 0 0 auto;
        background: #ced4da;
      }
      .flow-status-up      { background: #2dce89; }
      .flow-status-down    { background: #f5365c; }
      .flow-status-warning { background: #fb6340; }
      .flow-status-unknown { background: #ced4da; }

      .flow-entity {
        flex: 1 1 auto;
        font-size: 0.9rem;
        font-weight: 600;
        color: #32325d;
      }
      .flow-counts {
        display: inline-flex;
        gap: 0.4rem;
      }
      .flow-count {
        font-size: 0.78rem;
        font-weight: 700;
        padding: 0.12rem 0.5rem;
        border-radius: 999px;
        i { margin-right: 0.25rem; }
      }
      .flow-count-success { color: #1aae6f; background: rgba(45, 206, 137, 0.12); }
      .flow-count-pending { color: #d63b12; background: rgba(251, 99, 64, 0.12); }
      .flow-count-error   { color: #f5365c; background: rgba(245, 54, 92, 0.12); }

      .flow-synced {
        font-size: 0.78rem;
        color: #8898aa;
        white-space: nowrap;
        min-width: 7rem;
        text-align: right;
      }
    }

    .flow-records {
      padding: 0.5rem 0.9rem 0.9rem;
      background: #fbfcfe;
      border-top: 1px solid #f1f3f6;

      .flow-records-filter {
        display: flex;
        flex-wrap: wrap;
        gap: 1rem;
        margin-bottom: 0.75rem;

        .flow-filter-field {
          display: flex;
          flex-direction: column;
        }
        .flow-filter-actions {
          display: flex;
          align-items: flex-end;
          margin-left: auto;
        }
        .filter-label {
          font-size: 0.72rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.02em;
          color: #8898aa;
          margin-bottom: 0.3rem;
        }
      }
    }

    // ---- Lynexus heartbeat panel ----
    .heartbeat-toolbar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex-wrap: wrap;
      gap: 0.5rem;
      margin-bottom: 1rem;

      .heartbeat-intro {
        font-size: 0.9rem;
        color: #525f7f;
      }
      .heartbeat-toolbar-right {
        display: flex;
        align-items: center;
        gap: 0.75rem;
      }
      .heartbeat-autorefresh {
        display: flex;
        align-items: center;
        gap: 0.4rem;

        .heartbeat-autorefresh-label {
          font-size: 0.8rem;
          color: #8898aa;
        }
      }
      .heartbeat-stamp {
        font-size: 0.8rem;
        color: #8898aa;
        i { color: #2dce89; margin-right: 0.3rem; }
      }
    }

    .heartbeat-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
      gap: 1.25rem;
    }

    // Heartbeat graph modal chart container
    .heartbeat-graph-toolbar {
      display: flex;
      justify-content: flex-end;
      margin-bottom: 1rem;
    }
    .heartbeat-graph-wrap {
      position: relative;
      height: 340px;
    }

    // Heartbeat history table section (below the cards)
    .heartbeat-history {
      margin-top: 2rem;
      padding-top: 1.5rem;
      border-top: 1px solid #e9ecef;

      // Status pills matching the heartbeat card colors
      .log-status-hb-up      { background: #2dce89; color: #fff; }
      .log-status-hb-warning { background: #fb6340; color: #fff; }
      .log-status-hb-down    { background: #f5365c; color: #fff; }
      .log-status-hb-unknown { background: #ced4da; color: #525f7f; }
    }

    // ===== Health summary banner (above the grid) =====
    .hb-summary {
      display: flex;
      align-items: center;
      gap: 0.6rem;
      padding: 0.7rem 1rem;
      margin-bottom: 1rem;
      border-radius: 0.6rem;
      font-weight: 700;
      font-size: 0.95rem;
      border: 1px solid transparent;

      .hb-summary-icon { font-size: 1.15rem; }

      &.hb-summary-up {
        background: rgba(45, 206, 137, 0.1);
        border-color: rgba(45, 206, 137, 0.35);
        color: #1aae6f;
      }
      &.hb-summary-warning {
        background: rgba(251, 99, 64, 0.12);
        border-color: rgba(251, 99, 64, 0.4);
        color: #d63b12;
        animation: hb-alarm-soft 2s ease-in-out infinite;
      }
      &.hb-summary-down {
        background: rgba(245, 54, 92, 0.14);
        border-color: rgba(245, 54, 92, 0.45);
        color: #f5365c;
        animation: hb-alarm-soft 1.1s ease-in-out infinite;
      }
      &.hb-summary-unknown {
        background: rgba(136, 152, 170, 0.1);
        border-color: rgba(136, 152, 170, 0.3);
        color: #8898aa;
      }
    }

    .heartbeat-card {
      background: #fff;
      border: 1px solid #e9ecef;
      border-radius: 0.75rem;
      padding: 0;
      overflow: hidden;
      box-shadow: 0 0 1.5rem 0 rgba(136, 152, 170, 0.1);
      transition: box-shadow 0.2s ease, transform 0.2s ease;

      // Calm, healthy states
      &.hb-up      { border-color: rgba(45, 206, 137, 0.4); }
      &.hb-unknown { border-color: #e9ecef; }

      // Alarming states: flood the card and lift it so it cannot be missed.
      &.hb-warning {
        border-color: rgba(251, 99, 64, 0.55);
        background: rgba(251, 99, 64, 0.04);
      }
      &.hb-down {
        border-color: rgba(245, 54, 92, 0.6);
        background: rgba(245, 54, 92, 0.05);
        box-shadow: 0 0 0 1px rgba(245, 54, 92, 0.25), 0 0.5rem 1.5rem rgba(245, 54, 92, 0.18);
      }
      &.hb-alarm { animation: hb-card-alarm 1.4s ease-in-out infinite; }
      &.hb-down.hb-alarm { animation-duration: 1.1s; }

      // ===== Bold status banner (top strip of the card) =====
      .hb-banner {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.55rem 1.25rem;
        font-weight: 800;
        font-size: 0.8rem;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: #fff;

        .hb-banner-icon { font-size: 0.95rem; }
        .hb-banner-label { flex: 1 1 auto; }
      }
      .hb-banner-up      { background: #2dce89; }
      .hb-banner-down    { background: #f5365c; }
      .hb-banner-warning { background: #fb6340; }
      .hb-banner-unknown { background: #8898aa; }

      .hb-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 0.75rem;
        padding: 1.1rem 1.25rem 0;
      }
      .hb-identity {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
      }
      .hb-icon {
        color: #8898aa;
        font-size: 1rem;
      }
      .hb-name {
        font-size: 1.1rem;
        font-weight: 700;
        color: #32325d;
      }

      // Pulsing heartbeat dot in the banner
      .hb-pulse {
        width: 0.7rem;
        height: 0.7rem;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.85);
        position: relative;
        flex: 0 0 auto;
      }
      .hb-pulse-up      { animation: hb-pulse-anim 1.6s infinite; }
      .hb-pulse-down    { animation: hb-pulse-anim-strong 0.9s infinite; }
      .hb-pulse-warning { animation: hb-pulse-anim 1.2s infinite; }
      .hb-pulse-unknown { background: rgba(255, 255, 255, 0.6); }

      .hb-processed {
        font-size: 0.8rem;
        color: #8898aa;
        strong { color: #32325d; font-size: 0.95rem; }
      }

      .hb-events {
        list-style: none;
        margin: 0;
        padding: 0 1.25rem 1.1rem;
      }
      .hb-event {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.4rem 0;
        border-top: 1px solid #f1f3f6;
        font-size: 0.85rem;

        &:first-child { border-top: none; }
      }
      .hb-event-icon {
        width: 1rem;
        text-align: center;
        font-size: 0.8rem;
      }
      .hb-event-start { color: #2dce89; }
      .hb-event-stop  { color: #8898aa; }
      .hb-event-error { color: #f5365c; }
      .hb-event-label {
        flex: 1 1 auto;
        color: #525f7f;
      }
      .hb-event-time {
        color: #32325d;
        font-weight: 600;
        white-space: nowrap;
      }

      .hb-error-message {
        margin: 0 1.25rem 1.1rem;
        padding: 0.5rem 0.75rem;
        background: rgba(245, 54, 92, 0.1);
        color: #f5365c;
        font-weight: 600;
        border-radius: 0.4rem;
        font-size: 0.8rem;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }

    @keyframes hb-pulse-anim {
      0%   { box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.6); }
      70%  { box-shadow: 0 0 0 0.5rem rgba(255, 255, 255, 0); }
      100% { box-shadow: 0 0 0 0 rgba(255, 255, 255, 0); }
    }

    // Stronger, faster pulse for offline cards
    @keyframes hb-pulse-anim-strong {
      0%   { box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.85); transform: scale(1); }
      60%  { box-shadow: 0 0 0 0.6rem rgba(255, 255, 255, 0); transform: scale(1.15); }
      100% { box-shadow: 0 0 0 0 rgba(255, 255, 255, 0); transform: scale(1); }
    }

    // Card-level alarm: a subtle red/amber "breathing" glow so problem cards draw the eye.
    @keyframes hb-card-alarm {
      0%, 100% { box-shadow: 0 0 0 1px rgba(245, 54, 92, 0.15), 0 0.25rem 0.75rem rgba(245, 54, 92, 0.1); }
      50%      { box-shadow: 0 0 0 2px rgba(245, 54, 92, 0.45), 0 0.5rem 1.5rem rgba(245, 54, 92, 0.3); }
    }

    // Gentle opacity breathing for the summary banner when degraded/offline.
    @keyframes hb-alarm-soft {
      0%, 100% { opacity: 1; }
      50%      { opacity: 0.72; }
    }

    // currentColor pulse ring for status-badge dots (works on light backgrounds).
    @keyframes badge-dot-pulse {
      0%   { box-shadow: 0 0 0 0 currentColor; opacity: 1; }
      70%  { box-shadow: 0 0 0 0.35rem rgba(0, 0, 0, 0); opacity: 0.85; }
      100% { box-shadow: 0 0 0 0 rgba(0, 0, 0, 0); opacity: 1; }
    }

    // Respect users who prefer no motion.
    @media (prefers-reduced-motion: reduce) {
      .heartbeat-card.hb-alarm,
      .hb-summary-down,
      .hb-summary-warning,
      .hb-pulse-up,
      .hb-pulse-down,
      .hb-pulse-warning,
      .status-badge .status-dot { animation: none !important; }
    }

    // Readable status pills inside the table
    .log-status {
      display: inline-block;
      min-width: 92px;
      text-align: center;
      font-size: 0.75rem;
      font-weight: 700;
      padding: 0.2rem 0.6rem;
      border-radius: 999px;
      text-transform: uppercase;
      color: #fff;

      &.log-status-success { background: #2dce89; }
      &.log-status-error { background: #f5365c; }
      &.log-status-pending { background: #fb6340; }
    }

    // Keep long messages on one line with an ellipsis; full text in tooltip
    .message-cell {
      display: inline-block;
      max-width: 240px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      vertical-align: bottom;
    }
  }

  // Heading row: title on the left, full screen toggle on the right
  .integration-dashboard .page-heading {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;

    .fullscreen-toggle i {
      margin-right: 6px;
    }
  }

  // Full screen: cover the sidebar / navbar. z-index stays below Ant Design
  // popups (1050) so dropdowns and date pickers still show on top.
  .integration-dashboard.integration-dashboard--fullscreen {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 1000;
    overflow-y: auto;
    background: #f8f9fe;
  }

  // Resizable / reorderable columns on the document log tables
  .integration-dashboard {
    .ant-table-thead > tr > th.adjustable-th {
      position: relative;
      cursor: grab;
      user-select: none;

      // Drop target highlight while dragging another header over this one
      &.adjustable-th--over {
        box-shadow: inset 3px 0 0 #5e72e4;
        background: #f0f3ff;
      }

      // Drag handle on the right edge for resizing
      .adjustable-th__handle {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        width: 8px;
        cursor: col-resize;
        z-index: 1;

        &::after {
          content: '';
          position: absolute;
          top: 25%;
          bottom: 25%;
          right: 3px;
          width: 2px;
          background: #ced4da;
        }

        &:hover::after {
          background: #5e72e4;
        }
      }
    }
  }
</style>
